/**
 * DOM panels rendered from the PlayerView. Buttons carry data-* attributes; main.ts handles all
 * clicks through one delegated listener, so panels can be re-rendered freely. Anything with a
 * data-ref shows its Pathfinder-style rules block on hover (see rules.ts).
 */
import type {
  ActionView, BankItemView, BankView, ChamberView, CharacterSheetView, CreatureView, HandSlot, ItemView, PickupItemView, PickupSquareView, PlayerView, ShopItemView, SpecialWareView, StrikeView,
} from '../shared/view.ts';
import { currentTalk, talkOptions } from './talk.ts';
import { renderChat } from './chat.ts';
import { blockHtml, glyph, refAttr } from './rules.ts';
import { ATTRIBUTES, ATTRIBUTE_NAMES } from '../engine/rules/attributes.ts';
import { controlsHtml, listHtml, newListState, type ListCfg, type PickOpt } from './picklist.ts';
import { factionColor, statusLine } from './status.ts';
import { activeActions, me, store } from './store.ts';
import { tokenUrl } from './tokenizer.ts';
import type { Command } from '../shared/protocol.ts';
import type { BridgePlanView, CombatantView, EnchantRequestView, EnchantScrollView, EmporiumOfferView, ItemProficiencyView } from '../shared/view.ts';
import type { EnchantChoice } from './store.ts';

/** Timers for the press guard: the window's, or fakes in tests. */
export interface GuardTimers {
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(id: number): void;
}

/**
 * setHtml's cache and press guard. Views stream in up to 5 times a second, and replacing a panel
 * between mousedown and mouseup swallows the click, so while the pointer is pressed inside a
 * panel its new HTML waits until the pointer comes back up (or MAX_HOLD_MS passes). A box you are
 * typing in that carries `data-keep-focus` gets the cursor back after a rewrite; otherwise your
 * next keys would reach the window's shortcuts (Enter ends your turn, digits fire actions).
 */
export class RenderGuard {
  /** The longest a panel waits, so a lost pointerup can't freeze it. */
  static readonly MAX_HOLD_MS = 1000;
  private readonly cache = new WeakMap<Element, string>();
  private readonly held = new Map<Element, { html: string; patch?: () => void }>();
  private readonly timers: GuardTimers;
  private pressed: Node | null = null;
  private deadline = 0;

  constructor(timers: GuardTimers) {
    this.timers = timers;
  }

  /** Writes `html` into `el` unless it's already there (or held back); `patch` then fills in live values. */
  write(el: Element, html: string, patch?: () => void): void {
    if (this.cache.get(el) === html) this.held.delete(el);
    else if (this.pressed && el.contains(this.pressed)) this.held.set(el, { html, patch });
    else {
      this.held.delete(el);
      this.cache.set(el, html);
      const active = el.ownerDocument?.activeElement as HTMLElement | null | undefined;
      const keep = active && active !== el && el.contains(active) ? active.dataset?.keepFocus : undefined;
      el.innerHTML = html;
      if (keep) [...el.querySelectorAll<HTMLElement>('[data-keep-focus]')].find((n) => n.dataset.keepFocus === keep)?.focus();
    }
    patch?.();
  }

  /** The pointer went down on `target`: the panels around it hold their new HTML until release(). */
  press(target: Node | null): void {
    this.pressed = target;
    this.timers.clearTimeout(this.deadline);
    this.deadline = this.timers.setTimeout(() => this.release(), RenderGuard.MAX_HOLD_MS);
  }

  /** The pointer came up (or the press was lost): held HTML lands after the click reaches the old buttons. */
  release(): void {
    if (!this.pressed) return;
    this.pressed = null;
    this.timers.clearTimeout(this.deadline);
    this.timers.setTimeout(() => this.flush(), 0);
  }

  /** Writes every panel's held-back HTML now. */
  flush(): void {
    const held = [...this.held];
    this.held.clear();
    for (const [el, h] of held) this.write(el, h.html, h.patch);
  }
}

const guard = new RenderGuard({ setTimeout: (fn, ms) => window.setTimeout(fn, ms), clearTimeout: (id) => window.clearTimeout(id) });

/**
 * Sets a panel's HTML when it changed. `patch` fills in values that tick (countdowns, fuel) and are
 * kept out of the HTML so it stays the same between views; it runs on every call, and again when
 * HTML held back by the press guard lands.
 */
export function setHtml(el: Element, html: string, patch?: () => void): void {
  guard.write(el, html, patch);
}

/** Holds back panel re-renders while the pointer is pressed inside them (see RenderGuard). Call once. */
export function installRenderGuard(doc: Document = document): void {
  doc.addEventListener('pointerdown', (e) => guard.press(e.target as Node | null), true);
  for (const type of ['pointerup', 'pointercancel', 'dragend']) doc.addEventListener(type, () => guard.release(), true);
  (doc.defaultView ?? window).addEventListener('blur', () => guard.release());
}

/** Sets an element's text only when it changed (a live value patched into stable HTML). */
function setText(el: Element | null, text: string): void {
  if (el && el.textContent !== text) el.textContent = text;
}

export function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

export const signed = (n: number) => (n >= 0 ? `+${n}` : `–${Math.abs(n)}`);

export function costLabel(cost: number | string | undefined): string {
  if (cost === undefined) return '';
  if (cost === 'reaction' || cost === -1) return '⟲';
  if (cost === 'free' || cost === 0) return '◇';
  return '◆'.repeat(Number(cost));
}

export function realTimeLeft(view: PlayerView, gameSeconds: number): string {
  const real = Math.max(0, Math.round(gameSeconds / Math.max(0.0001, view.config.timeScale)));
  return `${Math.floor(real / 60)}:${String(real % 60).padStart(2, '0')}`;
}

/** Monster health as the party would describe it (exact Hit Points are shown only for the party). */
export const HEALTH_LABEL: Record<CreatureView['health'], string> = {
  unhurt: 'Unharmed', scratched: 'Barely Injured', wounded: 'Injured', bloodied: 'Badly Injured',
  'near death': 'Near Death', down: 'Unconscious', dead: 'Dead',
};

/** A creature's health label; a construct that's down has broken down. */
function healthLabel(c: CreatureView): string {
  return c.construct && c.health === 'down' ? 'Broken' : HEALTH_LABEL[c.health];
}

// ------------------------------------------------------------ top bar & turn bar

export function renderTopbar(view: PlayerView): void {
  const where = document.getElementById('where')!;
  const lightEl = document.getElementById('light-clock')!;
  if (view.phase === 'hub' || !view.dungeon) {
    where.textContent = 'Town';
    where.title = '';
    lightEl.textContent = '';
    return;
  }
  where.textContent = `Depth ${view.dungeon.depth} · ${view.dungeon.danger}`;
  where.title = view.dungeon.dangerNote;
  const light = view.sheet?.light;
  lightEl.textContent = light
    ? `${light.name}: ${light.lit ? `${realTimeLeft(view, light.fuel)} remaining` : 'unlit'}`
    : 'No light source held';
  lightEl.className = light?.lit ? 'tone-light' : 'bad';
}

/** Is the computer playing your character for you (autopilot)? */
export function yourAutopilot(view: PlayerView): boolean {
  return !!view.party.find((p) => p.isYou)?.autopilot;
}

/** While your autopilot is on: a chip at the top of the map that takes your character back. */
function autopilotChip(view: PlayerView): string {
  return yourAutopilot(view)
    ? '<button type="button" class="autopilot-chip" data-autopilot="off" title="The computer is playing your character as it plays a bot companion. Click (or press P) to take control again.">🤖 Autopilot · Take Control<span>P</span></button>'
    : '';
}

export function renderTurnbar(view: PlayerView): void {
  const el = document.getElementById('turnbar')!;
  const pilot = autopilotChip(view);
  if (view.phase === 'hub') return setHtml(el, `<b>Town</b>${pilot}<span class="muted">Buy equipment, select feats, then mark yourself ready.</span>`);
  const c = view.combat;
  const k = view.crawl;
  if (!c && k) {
    // Shadowdark dungeon time: no turns outside encounters, everyone acts at once; the dungeon counts rounds.
    const check = k.dark
      ? '<span class="crawl-check dark" title="The whole party stands in darkness">⚠ Total darkness: an encounter check every round</span>'
      : `<span class="crawl-check" title="The floor is ${esc(k.danger)}: a check every ${k.checkEvery === 1 ? 'round' : `${k.checkEvery} rounds`}">${k.checkIn === 1 ? 'Encounter check next round' : `Encounter check in ${k.checkIn} rounds`}</span>`;
    // 🤫: Avoiding Notice where it works (dim light or darkness), so a monster seen won't start a fight.
    const quiet = '<span class="quiet" title="Avoiding Notice in dim light or darkness: seeing a monster won\'t start a fight">🤫</span> ';
    const chips = k.members.map((m) => `<span class="init-chip party crawl ${m.isYou ? 'you' : ''}"${m.quiet ? ' data-quiet' : ''}>${m.quiet ? quiet : ''}${esc(m.name)}</span>`).join('');
    return setHtml(el, `<b>Exploring · Round ${k.round}</b>${pilot}${check}${chips}`);
  }
  if (!c) {
    return setHtml(el, `<b>Exploration</b>${pilot}<span class="muted">WASD/arrows/numpad move · click to walk · F light · G pick up · X interact · Esc cancel</span>`);
  }
  // The sides in the order they act, each with its summed initiative (a '?' while any of it is unseen).
  const sides: CombatantView[][] = [];
  for (const o of c.order) {
    if (sides.at(-1)?.[0].side === o.side) sides.at(-1)!.push(o);
    else sides.push([o]);
  }
  const chips = sides.map((side) => {
    const party = side[0].side === 'party';
    const sum = side.every((o) => o.known) ? String(side[0].sideInitiative) : '?';
    const title = party ? "The party's initiative, every roll summed: its characters act together, then its allies" : "This side's initiative, every roll summed: its creatures act one after another";
    const members = side.map((o) => `<span class="init-chip ${o.faction} ${o.current ? 'current' : ''}">${esc(o.name)} <span class="muted">${o.known ? o.initiative : '?'}</span></span>`).join('');
    return `<span class="init-side ${party ? 'party' : ''} ${side.some((o) => o.current) ? 'current' : ''}"><span class="init-sum" title="${title}">${sum}</span>${members}</span>`;
  }).join('');
  const current = c.order.filter((o) => o.current);
  // The party's turn, yours done: who's still taking theirs.
  const waiting = c.partyTurn ? current.filter((o) => o.isPc).map((o) => o.name) : [];
  const yours = c.yourTurn
    ? '<span class="yourturn">Your Turn</span>'
    : `<span class="muted">${waiting.length ? `Waiting for ${esc(waiting.join(', '))}` : current.length ? `${esc(current[0].name)}'s turn` : 'Waiting…'}</span>`;
  setHtml(el, `<b>Encounter · Round ${c.round}</b>${pilot}${yours}${chips}`);
}

/** Actions, reaction and multiple attack penalty, shown large at the bottom of the map. */
export function renderHud(view: PlayerView): void {
  const el = document.getElementById('action-hud')!;
  const c = view.combat;
  const k = view.crawl;
  el.classList.toggle('hidden', (!c && !k) || view.phase === 'hub');
  el.classList.toggle('crawl', !c && !!k);
  if (!c && k) {
    // Dungeon time: how much of the round is spent (hourglass pips), how far the party can still walk before
    // the dungeon's clock moves, and when the next encounter check comes. Nothing to finish: there are no turns.
    const filled = Math.floor(k.spent);
    const pips = Array.from({ length: 3 }, (_, i) => `<span class="pip ${i < filled ? 'on' : ''}">◆</span>`).join('');
    const walk = k.walkLeft === null ? ''
      : `<div class="hud-move" title="Walking is free, but it takes time: every 3 Strides walked (or 3 actions taken) by anyone pass a round for the dungeon (hunters close in, the encounter check counts down)"><b>${k.walkLeft}</b><span>${k.walkLeft === 1 ? 'square' : 'squares'} to next round</span></div>`;
    const check = `<div class="hud-check ${k.dark ? 'dark' : ''}"><b>${k.dark ? '⚠' : k.checkIn}</b><span>${k.dark ? 'In darkness: a check every round' : k.checkIn === 1 ? 'Encounter check next round' : 'rounds to an encounter check'}</span></div>`;
    return setHtml(el, `
      <div class="hud-clock" title="A round of dungeon time: 3 Strides walked or 3 actions taken, by anyone; a failed try at an obstacle takes a whole round"><div class="pips">${pips}</div><span>Round ${k.round}</span></div>
      ${walk}${check}${stealthHud(view)}`);
  }
  if (!c) return setHtml(el, '');
  const mine = c.yourTurn;
  const ct = mine ? view.construct : null;
  if (ct && store.controlling) {
    // Spending the construct's actions.
    const pips = Array.from({ length: ct.maxActions }, (_, i) => `<span class="pip ${i < ct.actionsLeft ? 'on' : ''}">◆</span>`).join('');
    const map = ct.attacks ? `<div class="hud-map" title="Its multiple attack penalty"><b>${ct.attacks === 1 ? '–5' : '–10'}</b><span>MAP</span></div>` : '';
    const what = ct.autonomous ? 'Stride or Strike' : `${ct.actionsLeft} action${ct.actionsLeft === 1 ? '' : 's'} left`;
    return setHtml(el, `
      <div class="hud-actions mine construct"><div class="pips">${pips}</div><span>⚙ ${esc(ct.name)}: ${what}</span></div>
      ${map}
      <button type="button" class="hud-finish hud-control" data-control="off" title="Act as your character again (Tab)">Back to ${esc(view.sheet?.name ?? 'you')}<span>Tab</span></button>${stealthHud(view)}`);
  }
  const left = mine ? c.actionsLeft : 0;
  const pips = Array.from({ length: c.maxActions }, (_, i) => `<span class="pip ${i < left ? 'on' : ''}">◆</span>`).join('');
  const caption = mine ? `${left} action${left === 1 ? '' : 's'} left` : 'Not your turn';
  const map = mine && c.attacks ? `<div class="hud-map" title="Multiple attack penalty"><b>${c.attacks === 1 ? '–5' : '–10'}</b><span>MAP</span></div>` : '';
  let construct = '';
  if (ct && ct.actionsLeft > 0) {
    construct = `<button type="button" class="hud-finish hud-control" data-control="on" title="Spend your construct's actions: click a square to Stride, an enemy to Strike (Tab)">⚙ Control ${ct.actionsLeft === 1 && ct.autonomous ? '(1 action)' : `(${ct.actionsLeft})`}<span>Tab</span></button>`;
  } else if (ct) {
    const why = !ct.ready ? 'Broken down' : ct.maxActions > 0 ? 'Done this turn' : 'Command it to act';
    construct = `<div class="hud-construct" title="Your construct companion"><b>⚙</b><span>${why}</span></div>`;
  }
  setHtml(el, `
    <div class="hud-actions ${mine ? 'mine' : ''}"><div class="pips">${pips}</div><span>${caption}</span></div>
    <div class="hud-reaction ${c.reaction ? 'on' : ''}" title="${c.reaction ? 'Your reaction is available' : 'You have used your reaction this round'}"><b>⟲</b><span>${c.reaction ? 'Reaction ready' : 'Reaction used'}</span></div>
    ${map}${construct}${stealthHud(view)}`);
}

const LIGHT_WORDS: Record<NonNullable<PlayerView['stealth']>['light'], { icon: string; word: string }> = {
  bright: { icon: '☀', word: 'Bright light' },
  dim: { icon: '◐', word: 'Dim light' },
  dark: { icon: '●', word: 'Darkness' },
};

/** The exploration activities (effect ids): an icon, a name, and what the activity does. */
const ACTIVITY_WORDS: Record<string, { icon: string; name: string; title: string }> = {
  'avoiding-notice': {
    icon: '🤫', name: 'Avoiding Notice',
    title: "Avoiding Notice (in dim light or darkness): seeing a creature doesn't start a fight. When a fight starts, you roll Stealth for initiative and start undetected by each foe whose Perception DC you meet. Use Avoid Notice again to stop.",
  },
  'following-the-expert': {
    icon: '👣', name: 'Following the Expert',
    title: 'Following the Expert: you Avoid Notice behind an ally within 30 feet who is Avoiding Notice and at least an expert in Stealth, with a bonus to your Stealth by their rank. Use Follow the Expert again to stop.',
  },
  scouting: {
    icon: '🔭', name: 'Scouting',
    title: 'Scouting: when the next encounter begins, everyone in the party gets a +1 circumstance bonus to initiative. Use Scout again to stop.',
  },
  'anticipating-ambush': {
    icon: '👁️', name: 'Anticipating an Ambush',
    title: 'Anticipating an Ambush: you roll Perception for initiative, foes rolling Stealth for it take a penalty, and you may spot lurkers nearby as the encounter begins. You Search for traps as you go.',
  },
  searching: {
    icon: '🔎', name: 'Searching',
    title: 'Searching: within 15 feet of a hidden trap you can see, you get a secret Perception check to spot it, at half speed. Use Search again to stop.',
  },
};

/** "hidden from 2 and undetected by 1" foes: the counts as words (empty when both are 0). */
function lostFoesText(hidden: number, undetected: number): string {
  return [hidden ? `hidden from ${hidden}` : '', undetected ? `undetected by ${undetected}` : ''].filter(Boolean).join(' and ');
}

/**
 * The HUD's Stealth columns: the light on your space and whether you can Hide there, your exploration activity
 * (while exploring), and the foes your Stealth has you hidden from or undetected by. Display only, like the HUD.
 */
export function stealthHud(view: PlayerView): string {
  const s = view.stealth;
  if (!s) return '';
  const { icon, word } = LIGHT_WORDS[s.light];
  const lurker = s.light === 'bright' && s.countsAs !== 'bright' && s.canHide;
  const text = lurker ? `${word}, counts as dim for you (Lamplight Lurker)` : `${word} · you ${s.canHide ? 'can' : "can't"} Hide`;
  const title = lurker ? 'Lamplight Lurker: the bright light on your space counts as dim light for your Stealth. You can Hide, Sneak and Avoid Notice here.'
    : !s.canHide ? s.reason ?? "You can't Hide here"
    : s.light === 'bright' ? 'Bright light, but you can Hide here (a foe sees you only concealed, or nothing sees you).'
    : `${word} on your space: you can Hide, Sneak and Avoid Notice. A torch's bright light would give you away.`;
  const parts = [`<div class="hud-light ${s.light}" data-stealth-light="${s.light}" title="${esc(title)}"><b>${icon}</b><span>${esc(text)}</span></div>`];
  const act = s.activity ? ACTIVITY_WORDS[s.activity] ?? { icon: '🧭', name: s.activity, title: '' } : null;
  if (act && s.activity) {
    // A half-speed activity slows your walking (Legendary Sneak Avoids Notice at full speed).
    const slowed = !!view.crawl && !!view.sheet && view.crawl.speed < Math.max(1, Math.floor(view.sheet.speed / 5));
    const following = s.activity === 'following-the-expert' ? (s.expert ? `Following ${s.expert}` : 'Following the Expert · no expert in range') : act.name;
    // The engine's own word on whether Avoid Notice works here (the crawl chip's 🤫): a foe that sees you only
    // concealed lets you Hide, but not Avoid Notice in bright light.
    const quiet = !!view.crawl?.members.find((m) => m.isYou)?.quiet;
    const label = [following, slowed ? 'half speed' : '', s.activity === 'avoiding-notice' && !quiet ? 'no effect in bright light' : ''].filter(Boolean).join(' · ');
    parts.push(`<div class="hud-activity" data-stealth-activity="${esc(s.activity)}" title="${esc(act.title)}"><b>${act.icon}</b><span>${esc(label)}</span></div>`);
  }
  if (s.hidden || s.undetected) {
    const counts = [s.undetected ? `🥷${s.undetected}` : '', s.hidden ? `👤${s.hidden}` : ''].filter(Boolean).join(' ');
    parts.push(`<div class="hud-sneak" data-stealth-lost title="${esc(`Your Stealth has you ${lostFoesText(s.hidden, s.undetected)} of the foes you can see`)}"><b>${counts}</b><span>Unnoticed</span></div>`);
  }
  return parts.join('');
}

// ------------------------------------------------------------ action bar

// Stealth's actions come after Listen, so the digits of the older ones stay where players learned them.
export const HOTKEY_ORDER = ['strike', 'stride', 'step', 'raise-shield', 'demoralize', 'trip', 'disarm', 'light', 'extinguish', 'interact', 'listen', 'hide', 'sneak', 'seek', 'avoid-notice', 'ambush', 'search'];

function orderKey(view: PlayerView): string {
  return `crawler.hotbar.${view.sheet?.id ?? 'default'}`;
}

/** The player's saved hot bar order (action ids), or [] for the default. */
function savedOrder(view: PlayerView): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(orderKey(view)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function defaultRank(id: string): number {
  const i = HOTKEY_ORDER.indexOf(id);
  return i < 0 ? 99 : i;
}

/** Actions shown on the bar (and bound to 1-9), in the player's order. */
export function actionBarList(view: PlayerView): ActionView[] {
  const inCombat = !!view.combat;
  const shown = activeActions(view).filter((a) => a.group !== 'item' && a.id !== 'open-door' && a.id !== 'close-door' && (inCombat || (!a.combatOnly && a.id !== 'stride')));
  const saved = savedOrder(view);
  const rank = (id: string) => {
    const i = saved.indexOf(id);
    return i >= 0 ? i : 1000 + defaultRank(id);
  };
  return shown.sort((a, b) => rank(a.id) - rank(b.id));
}

/** Moves `fromId` to where `toId` is on the bar. Actions not shown right now keep their places. */
export function moveAction(view: PlayerView, fromId: string, toId: string): void {
  if (fromId === toId) return;
  const order = savedOrder(view);
  // Save every known action, so customizing the exploration bar keeps Strike first in combat.
  const known = [...new Set([...view.actions.map((a) => a.id), ...HOTKEY_ORDER])].sort((a, b) => defaultRank(a) - defaultRank(b));
  const all = [...order, ...known.filter((id) => !order.includes(id))];
  const from = all.indexOf(fromId);
  if (from < 0) return;
  all.splice(from, 1);
  const to = all.indexOf(toId);
  all.splice(to < 0 ? all.length : to + (to >= from ? 1 : 0), 0, fromId);
  try {
    localStorage.setItem(orderKey(view), JSON.stringify(all));
  } catch {
    // Without storage the order lasts until the page reloads.
  }
}

export function resetActionOrder(view: PlayerView): void {
  try {
    localStorage.removeItem(orderKey(view));
  } catch {
    // Nothing to reset.
  }
}

export function renderActionbar(view: PlayerView): void {
  const el = document.getElementById('actionbar')!;
  if (view.phase === 'hub' || !view.dungeon) return setHtml(el, '');
  const list = actionBarList(view);
  const armed = store.targeting?.action.id;
  const buttons = list.map((a, i) => {
    const spell = a.spell ? `<span class="rank">${a.spell.rank === 0 ? 'Cantrip' : `Spell ${a.spell.rank}`} · DC ${a.spell.castDc}</span>` : '';
    // A toggle (an exploration activity) that's on: lit, and its tooltip says how to stop it.
    const toggle = a.active === undefined ? '' : ` aria-pressed="${a.active}"`;
    // Pray glows while the god is listening to a prayer of yours (its answer comes when it comes).
    const listening = a.id === 'pray' && !!view.prayer?.listening;
    const note = a.active ? `${a.reason ? `${a.reason}. ` : ''}On: click to stop.` : listening ? 'The god is listening: its answer comes when it comes.' : a.reason;
    // aria-disabled (not disabled) so unavailable actions can still be dragged and explain themselves.
    return `<button class="act ${esc(a.group)} ${a.id === 'pray' ? 'pray' : ''} ${listening ? 'listening' : ''} ${a.spell?.lost ? 'lost' : ''} ${armed === a.id ? 'armed' : ''} ${a.active ? 'on' : ''}" draggable="true" data-act="${esc(a.id)}" aria-disabled="${!a.enabled}"${toggle}${refAttr(a.ref, note)}>${i < 9 ? `<span class="key">${i + 1}</span>` : ''}${esc(a.name)}${glyph(a.cost)}${spell}</button>`;
  });
  if (store.controlling && view.construct) {
    buttons.unshift(`<span class="act-label" title="Your construct's actions: click a square to Stride, an enemy to Strike">⚙ ${esc(view.construct.name)}</span>`);
    buttons.push(`<button class="act back" data-control="off" title="Act as your character again (Tab)">Back to ${esc(view.sheet?.name ?? 'you')}</button>`);
  } else if (savedOrder(view).length) buttons.push('<button class="act reset-order" data-reset-order title="Restore the default hot bar order">↺</button>');
  if (view.combat?.yourTurn) buttons.push('<button class="act end-turn" data-endturn title="End your turn (Space)">End Turn</button>');
  setHtml(el, buttons.join(''));
}

// ------------------------------------------------------------ log

export function renderLog(view: PlayerView): void {
  renderChat(document.getElementById('log')!, view.log);
}

// ------------------------------------------------------------ party

export function renderParty(view: PlayerView): void {
  setHtml(document.getElementById('party')!, partyHtml(view));
}

/** The party list: the players' characters, then the allies (in the dungeon, or waiting in town), then the quests. */
export function partyHtml(view: PlayerView): string {
  const rows = view.party.map((p) => {
    const hp = p.hp !== null ? ` · ${p.hp}/${p.maxHp} HP` : '';
    const status = p.status === 'ok' || p.status === 'hub' ? '' : ` <span class="bad">${p.status === 'down' ? 'unconscious' : p.status}</span>`;
    const ready = view.phase === 'hub' ? (p.ready ? ' <span class="good">ready</span>' : ' <span class="muted">not ready</span>') : '';
    // Anyone can call a vote to remove another player (someone who had to leave mid-game).
    const remove = p.isYou || view.kickVote ? ''
      : `<button type="button" class="party-remove" data-remove-player="${esc(p.playerId)}" title="Call a vote to remove ${esc(p.characterName ?? p.playerName)} from the session">✕</button>`;
    // Autopilot: the computer plays the character (a badge for everyone; your own row has the switch).
    const pilot = p.isYou && p.characterName ? autopilotSwitch(!!p.autopilot)
      : p.autopilot ? ' <span class="autopilot-badge" title="The computer is playing this character for its player for now">🤖 autopilot</span>' : '';
    // Another player's character: its name opens the Give menu (client/transfer.ts).
    const bold = `<b>${esc(p.characterName ?? '(no character)')}</b>`;
    const name = p.transfer && p.characterId
      ? `<button type="button" class="party-name" data-transfer-open="${esc(p.characterId)}" title="Give ${esc(p.characterName ?? p.playerName)} items from your gear">${bold}</button>` : bold;
    return `<div class="party-row ${p.autopilot ? 'autopilot' : ''}"><span class="dot ${p.connected ? '' : 'off'}"></span>${avatar(p.token, p.characterName ?? '?', 'small')}<span class="grow">${name} <span class="muted">${esc(p.className ?? '')} ${p.level ? `${p.level}` : ''} · ${esc(p.playerName)}${p.isYou ? ' (you)' : ''}</span>${hp}${status}${ready}</span>${pilot}${remove}</div>`;
  });
  const allies = (view.dungeon?.creatures ?? []).filter((c) => c.ally).map((c) => {
    const bond = view.companions.find((k) => k.id === c.id);
    const dying = c.status?.find((s) => s.id === 'dying');
    const state = dying ? ` <span class="bad">dying ${dying.value ?? 1}</span>` : '';
    return `<div class="party-row"${bond ? ` title="${esc(companionTitle(bond))}"` : ''}><span class="dot ally"></span><span class="grow"><b>${esc(c.name)}</b>${allyBadge(c.allyUntil, bond?.withName)} <span class="muted">ally${bond ? ` ${bond.level} · with ${esc(bond.withName)}` : ''} · ${esc(healthLabel(c))}</span>${state}</span></div>`;
  });
  // In town, the allies who joined for good wait with their characters for the next delve.
  if (view.phase === 'hub') {
    for (const k of view.companions) {
      const part = k.yours ? `<button type="button" class="party-remove" data-dismiss-companion="${esc(k.id)}" title="Part ways with ${esc(k.name)}: it leaves the party for good">✕</button>` : '';
      allies.push(`<div class="party-row companion" title="${esc(companionTitle(k))}"><span class="dot ally"></span><span class="grow"><b>${esc(k.name)}</b>${allyBadge('good', k.withName)} <span class="muted">${esc(k.kind)} ${k.level} · ${esc(k.attitude)} · with ${esc(k.withName)}</span></span>${part}</div>`);
    }
  }
  const quests = view.quests.map((q) => `<div class="quest ${q.done ? 'done' : q.failed ? 'failed' : ''}">${q.done ? '✓' : q.failed ? '✗' : '◇'} ${esc(q.text)}</div>`);
  // What the room's Artifacts of Change did to its dungeon, for good.
  const changes = (view.roomChanges ?? []).map((t) => `<div class="room-change">◈ ${esc(t)}</div>`);
  return `<h2>Party</h2>${rows.join('')}${allies.join('')}${quests.length ? `<h2>Quests</h2>${quests.join('')}` : ''}`
    + (changes.length ? `<h2 title="Artifacts of Change touched in this room. They last for good, for every delve from it.">Changes</h2>${changes.join('')}` : '');
}

/** Your autopilot switch in the party list: on, the computer plays your character until you switch it off. */
function autopilotSwitch(on: boolean): string {
  const title = on
    ? 'Autopilot is on: the computer plays your character (following the party, keeping a light, fighting). Click or press P to take control again.'
    : 'Autopilot: let the computer play your character the way it plays a bot companion until you switch it off (P). It never makes your level-up choices.';
  return `<button type="button" class="party-autopilot ${on ? 'on' : ''}" data-autopilot="${on ? 'off' : 'on'}" aria-pressed="${on}" title="${title}">🤖 ${on ? 'Autopilot on' : 'Autopilot'}</button>`;
}

/** The badge by an ally's name: how long it stays with the party, explained in its tooltip. */
const ALLY_TERMS: Record<NonNullable<CreatureView['allyUntil']>, { label: string; title: (withName?: string) => string }> = {
  floor: { label: 'this floor', title: () => 'Joined for this floor only: it stays behind when the party takes the stairs down.' },
  delve: { label: 'this delve', title: () => 'Joined for this delve: it follows the party down the stairs, but stays in the dungeon when the party leaves.' },
  good: {
    label: 'for good',
    title: (w) => `Joined for good: it travels with ${w ?? 'its character'} between delves, keeping its gear and memories, until it dies or parts ways.`,
  },
  encounter: { label: 'this fight', title: () => 'Pressed into this fight: it fights beside the party until the encounter ends, then stands aside again.' },
};

export function allyBadge(term: CreatureView['allyUntil'], withName?: string): string {
  const t = term ? ALLY_TERMS[term] : undefined;
  return t ? ` <span class="ally-term ${term}" title="${esc(t.title(withName))}">${t.label}</span>` : '';
}

/** A companion's tooltip: what it carries, and what it remembers of its time with the party. */
function companionTitle(k: PlayerView['companions'][number]): string {
  const gear = k.gear.length ? `Gear: ${k.gear.join(', ')}.` : 'No gear.';
  const delves = `${k.delves} ${k.delves === 1 ? 'delve' : 'delves'} with the party.`;
  const growth = k.level < k.levelCap
    ? `Grows with experience up to level ${k.levelCap}, as ${k.withName} grows.`
    : `Grows no stronger until ${k.withName} reaches level ${k.growsAt}.`;
  return [`${k.name}, ${k.kind} (level ${k.level}), travels with ${k.withName}. ${delves}`, growth, gear, ...k.memories.map((m) => `• ${m}`)].join('\n');
}

/** The chat bar: who hears you (pick from everyone in earshot), and whether they're thinking of a reply. */
export function renderTalk(view: PlayerView): void {
  const select = document.getElementById('talk-to') as HTMLSelectElement;
  const status = document.getElementById('talk-target')!;
  const input = document.getElementById('chat-input') as HTMLInputElement;
  const options = talkOptions(view);
  const current = currentTalk(view, options);
  // Whoever you picked is out of earshot now: back to the natural choice.
  if (store.talkTo && store.talkTo !== current.value) store.talkTo = null;
  const signature = options.map((o) => `${o.value}|${o.label}`).join(';');
  if (select.dataset.sig !== signature && document.activeElement !== select) {
    select.dataset.sig = signature;
    select.innerHTML = options.map((o) => `<option value="${esc(o.value)}">${esc(o.label)}</option>`).join('');
  }
  if (select.value !== current.value && document.activeElement !== select) select.value = current.value;
  select.classList.toggle('to-creature', !!current.creatureId || !!current.creatureIds || current.value.startsWith('e:'));
  select.classList.toggle('to-players', current.value === 'players');
  select.classList.toggle('to-recall', !!current.recallId);
  const ai = view.config.gm === 'ai' || view.config.agents === 'ai' || view.config.lore === 'ai';
  const gm = ai ? '<span class="gm-badge" title="An AI Game Master runs NPCs, puzzles and every thinking creature, and reads Recall Knowledge questions">AI GM</span>' : '';
  setHtml(status, `${gm}${current.thinking ? `<span class="thinking">${esc(current.name ?? 'They')} is thinking…</span>` : ''}`);
  if (input.placeholder !== current.placeholder) input.placeholder = current.placeholder;
  renderShop(view);
  renderFactionQuests(view);
  renderBank(view);
  renderPrompt(view);
  renderRecall(view);
}

/**
 * Faction quests above the chat bar: an offer from a giver in earshot (Accept, Decline), one you turned down
 * (Reconsider), and each errand you took (Hand over, Buy, Steal, Request, Coerce, Give up), greyed out with the
 * reason when not now. Done and failed ones live in the journal and the party list.
 */
function renderFactionQuests(view: PlayerView): void {
  const el = document.getElementById('quest-box')!;
  const cards = view.factionQuests.filter((q) => q.status === 'offered' || q.status === 'declined' || q.status === 'taken');
  if (!cards.length) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  setHtml(el, cards.map(factionQuestHtml).join(''));
}

/** One faction quest's card. */
export function factionQuestHtml(q: PlayerView['factionQuests'][number]): string {
  const item = `<span class="ref"${refAttr(q.itemRef)}>${esc(q.item)}</span>`;
  const button = (data: string, label: string, title: string, blocked?: string, ghost = false) =>
    `<button type="button" class="small${ghost ? ' ghost' : ''}" ${data}${blocked ? ' disabled' : ''} title="${esc(blocked ?? title)}">${label}</button>`;
  const id = esc(q.id);
  if (q.status === 'declined') {
    return `<div class="quest-card declined"><span>You turned down ${esc(q.giver)}'s errand: the ${item}.</span>`
      + button(`data-quest="accept:${id}"`, 'Reconsider', `Take ${q.giver}'s errand after all`) + '</div>';
  }
  const rewardText = `Reward: a ${q.reward.favor} favor with ${q.faction}, ${q.reward.gold} gp`;
  const reward = `<span class="quest-reward">${esc(rewardText)}</span>`;
  if (q.status === 'offered') {
    return `<div class="quest-card"><span class="quest-title">❗ ${esc(q.giver)} <span class="muted">(${esc(q.faction)})</span></span>`
      + `<span class="quest-words">“${esc(q.text)}”</span>${reward}`
      + button(`data-quest="accept:${id}"`, 'Accept', `Take the errand: bring the ${q.item} back from ${q.holder}`)
      + button(`data-quest="decline:${id}"`, 'Decline', 'Turn it down (you can still take it later while it is open)', undefined, true) + '</div>';
  }
  const carried = q.carriedBy ? ` <span class="good">${esc(q.carriedBy)} has it.</span>` : '';
  const parts = [`<span class="quest-title">◇ The ${item}</span><span class="quest-words">from ${esc(q.holder)} (${esc(q.where)}) to ${esc(q.giver)}.${carried}</span>`];
  if (q.can.turnIn) parts.push(button(`data-quest="turnIn:${id}"`, `Hand over the ${esc(q.item)}`, `Give it back to ${q.giver}. ${rewardText}`, q.can.turnIn.blocked));
  if (q.can.buy) parts.push(button(`data-quest="buy:${id}"`, `Buy it, ${q.can.buy.price} gp`, `Pay ${q.holder} ${q.can.buy.price} gp for it`, q.can.buy.blocked));
  for (const a of q.approaches ?? []) {
    const odds = a.dc !== undefined ? `${a.skill} DC ${a.dc}${a.chance !== undefined ? ` · ${Math.round(a.chance * 100)}%` : ''}` : a.skill;
    parts.push(button(`data-quest-act="${esc(a.actionId)}:${esc(a.holderId)}"`, esc(a.name), odds, a.blocked ? `${odds}: ${a.blocked}` : undefined));
  }
  if (q.can.abandon) parts.push(button(`data-quest="abandon:${id}"`, 'Give up', 'Give up the errand (no penalty)', undefined, true));
  return `<div class="quest-card">${parts.join('')}</div>`;
}

/** A check a creature asked your character for: roll it or decline. */
function renderPrompt(view: PlayerView): void {
  const el = document.getElementById('prompt')!;
  const p = view.prompt;
  if (!p) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const mod = p.modifier >= 0 ? `+${p.modifier}` : `${p.modifier}`;
  const pay = p.payment ? [p.payment.gold ? `${p.payment.gold} gp` : '', p.payment.item ? `your ${p.payment.item.toLowerCase()}` : ''].filter(Boolean).join(' and ') : '';
  // The buttons stay put while the countdown ticks, so a click never lands on a button being replaced.
  const countdown = () => setText(el.querySelector('.prompt-time'), `${p.secondsLeft}s`);
  if (p.payment) {
    setHtml(el, `<span class="prompt-title">💰 <b>${esc(p.from)}</b> asks you for <b>${esc(pay)}</b></span>`
      + `<span class="prompt-reason">${esc(p.reason)}</span>`
      + `<button class="small" data-check-roll="${esc(p.id)}">Hand over ${esc(pay)}${p.cost ? ' (1 action)' : ''}</button>`
      + `<button class="small ghost" data-check-decline="${esc(p.id)}">Refuse</button>`
      + '<span class="muted prompt-time" title="Seconds left to decide"></span>', countdown);
  } else {
    // Why the DC is what it is: the creature's own DC, and what its morale and nature add.
    const signed = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
    const why = p.dcModifiers?.length ? `: ${p.baseDc} ${p.dcModifiers.map((m) => `${signed(m.value)} ${m.reason}`).join(', ')}` : '';
    const odds = p.chance !== undefined ? ` · ${Math.round(p.chance * 100)}%` : '';
    setHtml(el, `<span class="prompt-title">⚖ <b>${esc(p.from)}</b> calls for a <b>${esc(p.skill)}</b> check <span class="muted">(DC ${p.dc}${esc(why)})</span></span>`
      + `<span class="prompt-reason">${esc(p.reason)}</span>`
      + `<button class="small" data-check-roll="${esc(p.id)}" title="Roll ${esc(p.skill)} against DC ${p.dc}${odds ? `: about ${Math.round(p.chance! * 100)}% to succeed` : ''}">Roll ${esc(p.skill)} ${mod}${odds}${p.cost ? ' (1 action)' : ''}</button>`
      + `<button class="small ghost" data-check-decline="${esc(p.id)}">Decline</button>`
      + '<span class="muted prompt-time" title="Seconds left to decide"></span>', countdown);
  }
}

/** Your open Recall Knowledge question: ask about one thing with a topic button, or in words (the chat box is set to ask). */
function renderRecall(view: PlayerView): void {
  const el = document.getElementById('recall')!;
  const r = view.recall;
  if (!r) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  setHtml(el, recallHtml(r), () => setText(el.querySelector('.recall-time'), `${r.secondsLeft}s`));
}

/** The recall bar's HTML: the same while the countdown ticks (patched into .recall-time), so the buttons hold still. */
export function recallHtml(r: NonNullable<PlayerView['recall']>): string {
  const topics = r.topics.map((t) => `<button type="button" class="small" data-recall-topic="${esc(t.id)}"${r.thinking ? ' disabled' : ''}>${esc(t.label)}</button>`).join('');
  return `<span class="recall-title" title="Pick a topic, or type a question in the chat box (📖 Recall is picked)">📖 <b>Recall Knowledge (${esc(r.skill)}):</b> ${esc(r.creature)}. Ask about one thing.</span>`
    + topics
    + (r.thinking ? '<span class="thinking">The GM is thinking…</span>' : '')
    + '<span class="muted recall-time" title="Seconds left to ask"></span>';
}

/** A merchant's wares, as buy buttons above the chat bar. */
function renderShop(view: PlayerView): void {
  const el = document.getElementById('shop')!;
  // A merchant in earshot, or else a haven that trades with you.
  const shop = view.talk?.shop ?? view.havenShop;
  const seller = view.talk?.shop ? view.talk.name : view.havenShop?.name;
  if (!shop) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const wares = shop.wares.map((w) => `<button class="small ware" data-trade-buy="${esc(w.item)}" ${w.price > shop.gold ? 'disabled' : ''} title="Buy one for ${w.price} gp">`
    + `<span class="ref"${refAttr(w.ref)}>${esc(w.name)}</span>${w.qty > 1 ? ` <span class="muted">×${w.qty}</span>` : ''} <b>${w.price} gp</b></button>`).join('');
  setHtml(el, `<span class="shop-title">${esc(seller ?? '')}'s wares <span class="muted">(you carry ${shop.gold} gp)</span></span>${wares || '<span class="muted">Sold out.</span>'}`);
}

// ------------------------------------------------------------ the vault

/** "12/40 gear slots", or "12 gear slots" for a vault without a limit. */
function vaultSlots(bank: BankView): string {
  return `${bank.slotsUsed}${bank.slotsMax > 0 ? `/${bank.slotsMax}` : ''} gear slots`;
}

/** Withdraw buttons for a vault stack: as much as fits in your pack (greyed out, with the reason, when none does), and one at a time. */
function withdrawButtons(i: BankItemView): string {
  const why = i.fits === 0 ? "You don't have enough free gear slots"
    : i.fits < i.qty ? `Only ${i.fits} of ${i.qty} fit in your pack`
    : `Take ${i.qty > 1 ? `all ${i.qty}` : 'it'} into your pack`;
  const label = i.fits > 0 && i.fits < i.qty ? `Withdraw ${i.fits}` : 'Withdraw';
  const one = i.qty > 1 && i.fits > 1 ? `<button class="small ghost" data-bank-withdraw="${esc(i.uid)}:1" title="Take just one">Withdraw 1</button>` : '';
  return `${one}<button class="small" data-bank-withdraw="${esc(i.uid)}:${i.fits}"${i.fits ? '' : ' disabled'} title="${esc(why)}">${label}</button>`;
}

/** The Store button for one of your items: as much of a stack as the vault has room for, greyed out (with the reason) when it can't go in. */
function storeButton(view: PlayerView, i: ItemView): string {
  const bank = view.bank;
  if (!bank || !(i.uid in bank.deposit)) return '';
  const part = bank.partial[i.uid];
  if (part) return `<button class="small" data-bank-deposit="${esc(i.uid)}" data-bank-qty="${part}" title="Only ${part} of ${i.qty} fit in your vault">Store ${part}</button>`;
  const blocked = bank.deposit[i.uid];
  const why = blocked ?? (bank.where === 'courier'
    ? `Send it to your vault in town through ${bank.npc ?? 'the courier'}`
    : 'Keep it in your vault: stored items take no gear slots and grant no benefits');
  return `<button class="small" data-bank-deposit="${esc(i.uid)}"${blocked ? ' disabled' : ''} title="${esc(why)}">Store</button>`;
}

/** Your vault through a vault courier: each stack with its Withdraw buttons, above the chat bar. */
function renderBank(view: PlayerView): void {
  const el = document.getElementById('bank')!;
  const bank = view.bank;
  if (!bank || bank.where !== 'courier') {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const chips = bank.items.map((i) => `<span class="bank-chip"><span class="ref"${refAttr(i.ref, i.refNote)}>${esc(i.name)}</span>`
    + `${i.qty > 1 ? ` <span class="muted">×${i.qty}</span>` : ''}${withdrawButtons(i)}</span>`).join('');
  setHtml(el, `<span class="bank-title">Your vault, through ${esc(bank.npc ?? 'the courier')} <span class="muted">(${vaultSlots(bank)})</span></span>`
    + (chips || '<span class="muted">Empty. Use Store on items in your pack to send them to town.</span>'));
}

/** The town vault: one row per stack, with Withdraw buttons. Store is on each row of your gear. */
function vaultHtml(bank: BankView | null): string {
  if (!bank) return '';
  const rows = bank.items.map((i) => `<div class="vault-row">
      <span class="sr-name"><span class="ref"${refAttr(i.ref, i.refNote)}>${esc(i.name)}</span>${i.qty > 1 ? ` <span class="muted">×${i.qty}</span>` : ''}</span>
      <span class="sr-level">${levelTag(i.level)}</span>
      <span class="vr-slots muted">${i.slots ? `${i.slots} slot${i.slots === 1 ? '' : 's'}` : 'no slots'}</span>
      <span class="vr-buttons">${withdrawButtons(i)}</span>
    </div>`).join('');
  return `<h2>Vault <span class="muted">${vaultSlots(bank)}</span></h2>
    <div class="vault-list">${rows || '<span class="muted">Your vault is empty. Use Store on any row of your gear: stored items stay safe in town, take no gear slots and grant no benefits until withdrawn. A vault courier in the dungeon reaches the same vault.</span>'}</div>`;
}

// ------------------------------------------------------------ making camp

/** The Make Camp dialog: rest by a fire, burn spare torches into a campfire, or search for firewood. */
export function renderCamp(view: PlayerView): void {
  const el = document.getElementById('camp')!;
  const camp = view.camp;
  if (!store.campOpen || !camp || view.combat || view.scavenge) {
    store.campOpen = false;
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const option = (method: string, icon: string, title: string, text: string, enabled: boolean, why = '') =>
    `<button type="button" class="camp-option" data-camp="${method}" ${enabled && !camp.blocker ? '' : 'disabled'}${why ? ` title="${esc(why)}"` : ''}>`
    + `<span class="camp-icon">${icon}</span><span><b>${esc(title)}</b><span class="muted">${esc(text)}</span></span></button>`;
  const options: string[] = [];
  if (camp.fire) {
    options.push(option('rest', '🔥', 'Rest by the Fire', `Rest beside ${camp.fire}.`, true));
  } else {
    const enough = camp.torches >= camp.torchesNeeded;
    options.push(option('torches', '🕯️', `Burn ${camp.torchesNeeded} Torches`,
      `Bundle ${camp.torchesNeeded} spare torches into a campfire. The party has ${camp.torches} spare ${camp.torches === 1 ? 'torch' : 'torches'}.`,
      enough, enough ? '' : `You need ${camp.torchesNeeded} spare torches`));
  }
  if (camp.scavenge) {
    const s = camp.scavenge;
    options.push(option('scavenge', '🪵', 'Scavenge for Firewood',
      `${s.description} Survival DC ${s.dc} (${s.difficulty}). ${s.needed} ${s.needed === 1 ? 'success builds' : 'successes build'} a campfire; a critical success counts as 2. `
      + 'Everyone who joins searches each 10-minute round, and the party rolls 1 encounter check per round however many search.', true));
  }
  const warning = camp.blocker ? `<p class="bad">${esc(camp.blocker)}.</p>`
    : camp.rations ? '' : '<p class="bad">You carry no rations. Unless you can forage, you gain no benefit from the rest.</p>';
  setHtml(el, `
    <div class="modal-box camp-box" role="dialog" aria-label="Make Camp">
      <h1>Make Camp</h1>
      <p class="muted">The party rests for 8 hours beside a fire. Each character who eats a ration regains all Hit Points and every spell lost to a failed spellcasting check. Monsters might find the camp.</p>
      ${warning}
      <div class="camp-options">${options.join('')}</div>
      <div class="modal-actions"><button type="button" data-camp-close>Cancel</button></div>
    </div>`);
}

// ------------------------------------------------------------ enchanting

/** The chance (in percent) of each degree of success, critical failure first, for a d20 check. */
export function degreeOdds(modifier: number, dc: number): [number, number, number, number] {
  const out: [number, number, number, number] = [0, 0, 0, 0];
  for (let n = 1; n <= 20; n++) {
    const total = n + modifier;
    let d = total >= dc + 10 ? 3 : total >= dc ? 2 : total <= dc - 10 ? 0 : 1;
    if (n === 20) d = Math.min(3, d + 1);
    else if (n === 1) d = Math.max(0, d - 1);
    out[d] += 5;
  }
  return out;
}

/** The odds bar of a check: critical failure, failure, success and critical success, left to right. */
function oddsBar([cf, f, s, cs]: [number, number, number, number], cls = ''): string {
  return `<div class="ench-bar${cls}" aria-hidden="true"><span class="cf" style="width:${cf}%"></span><span class="f" style="width:${f}%"></span><span class="s" style="width:${s}%"></span><span class="cs" style="width:${cs}%"></span></div>`;
}

/** Gear an enchant scroll can be chosen for: it can go on now, or the bridging assistant leads up to it. */
export function enchantable(t: { error?: string; bridge?: BridgePlanView }): boolean {
  return !t.error || !!t.bridge;
}

/** A piece of gear to choose in the Enchant or Anoint dialog (gear whose slot needs bridging first, too). */
function gearChoice(view: PlayerView, t: { uid: string; name: string; error?: string; replaces?: string; blessed?: boolean; bridge?: BridgePlanView }, chosen: boolean): string {
  const item = view.sheet?.items.find((i) => i.uid === t.uid);
  const d = item?.durability;
  const steps = t.bridge?.steps.length ?? 0;
  const notes = [
    t.replaces ? `replaces ${esc(t.replaces)}` : '',
    d ? `${d.hp}/${d.max} HP` : '',
  ].filter(Boolean).join(' · ');
  const why = t.error ? `${esc(t.error)}${steps ? ` · bridge ${steps} ${steps === 1 ? 'rank' : 'ranks'} first` : ''}` : '';
  return `<button type="button" class="ench-target${chosen ? ' chosen' : ''}" data-ench-target="${esc(t.uid)}" ${enchantable(t) ? '' : `disabled title="${esc(t.error)}"`}>`
    + `<b>${esc(t.name)}</b>${t.blessed ? ' <span class="tag blessed">blessed</span>' : ''}${d?.broken ? ' <span class="tag broken">broken</span>' : ''}`
    + `${steps ? ' <span class="tag bridge">needs bridging</span>' : ''}`
    + `${notes ? ` <span class="muted">${notes}</span>` : ''}${why ? `<span class="why">${why}</span>` : ''}</button>`;
}

type EnchantPick = ReturnType<typeof enchantPick>;

/**
 * What an enchanting choice comes to: who reads the scroll (null: you) and their modifier, your powder (none
 * when it's left to them) and the DC after it, and the holy water that anoints the gear first (unless it's blessed).
 */
function enchantPick(view: PlayerView, c: EnchantChoice, check: { skill: string; modifier: number; dc: number }, blessed: boolean) {
  const items = view.sheet?.items ?? [];
  const who = view.enchanters.find((x) => x.characterId === c.enchanterId) ?? null;
  const theirs = !!who && c.powderFrom === 'enchanter';
  const powder = theirs ? undefined : items.find((i) => i.powder && i.uid === c.powderUid);
  const water = !blessed && c.anoint ? items.find((i) => i.anoint) : undefined;
  return {
    who, theirs, powder, water, blessed: blessed || !!water,
    modifier: who ? who.skills[check.skill] ?? 0 : check.modifier,
    dc: check.dc - (powder?.powder ?? 0),
  };
}

/** The enchant command for a pick, or the request to the party member who reads it. */
function enchantSend(scrollUid: string, targetUid: string, p: EnchantPick): Command {
  const o = { scrollUid, targetUid, ...(p.powder ? { powderUid: p.powder.uid } : {}), ...(p.water ? { waterUid: p.water.uid } : {}) };
  return p.who ? { type: 'requestEnchant', enchanterId: p.who.characterId, powderFrom: p.theirs ? 'enchanter' : 'requester', ...o } : { type: 'enchant', ...o };
}

/** A bridging step's scroll (the one picked, else the first you can read) and what its choices come to. */
function bridgeStep(view: PlayerView, step: BridgePlanView['steps'][number], c: EnchantChoice, blessed: boolean) {
  const opt = step.options.find((o) => o.uid === c.scrollUid && !o.blocked) ?? step.options.find((o) => !o.blocked);
  return { opt, pick: opt ? enchantPick(view, c, opt, blessed) : null };
}

/**
 * A scroll picked in the town's enchanting helper, as the Enchant dialog reads one: its check, and the helper's gear
 * as its one target, with a bridging plan built from that slot's own scrolls (each rank's options are the slot's
 * scrolls of that rank, best odds first). Undefined once the helper no longer lists it (laid, destroyed, moved).
 */
export function helperScroll(view: PlayerView, at: { gearUid: string; slot: 'prefix' | 'suffix' }, scrollUid: string): EnchantScrollView | undefined {
  const gear = view.hub?.enchanting?.gear.find((g) => g.uid === at.gearUid);
  const slot = gear?.[at.slot];
  const s = slot?.scrolls.find((x) => x.uid === scrollUid);
  if (!gear || !slot || !s) return undefined;
  const steps = s.bridge.map((b) => ({ ...b, options: slot.scrolls.filter((o) => o.rankIndex === b.rankIndex) }));
  const cur = slot.current;
  const bridge: BridgePlanView = {
    slot: at.slot, current: cur ? { name: cur.name, rank: cur.rank, rankIndex: cur.rankIndex } : null, goal: s.rank, goalIndex: s.rankIndex,
    steps, missing: steps.filter((x) => !x.options.some((o) => !o.blocked)).length,
  };
  const blessed = !!view.sheet?.items.find((i) => i.uid === gear.uid)?.blessed;
  return {
    line: s.line, name: s.name, slot: at.slot, rank: s.rank, rankIndex: s.rankIndex, skill: s.skill, modifier: s.modifier, dc: s.dc, loss: s.loss,
    text: s.text, ref: s.ref, ...(s.blocked ? { blocked: s.blocked } : {}), ...(s.damaged ? { damaged: true } : {}),
    targets: [{
      uid: gear.uid, name: gear.name, ...(blessed ? { blessed } : {}), ...(cur ? { replaces: `${cur.name} (rank ${cur.rank})` } : {}),
      ...(steps.length ? { error: 'Needs bridging', bridge } : {}),
    }],
  };
}

/** The scroll an enchanting pick reads: one in your pack (the Enchant dialog), or one the enchanting helper lists (pack or vault). */
function enchantScrollOf(view: PlayerView, st: NonNullable<typeof store.enchant>): EnchantScrollView | undefined {
  return st.helper ? helperScroll(view, st.helper, st.itemUid) : view.sheet?.items.find((i) => i.uid === st.itemUid)?.enchant;
}

/** The Enchant dialog's scroll and chosen gear (gear it can go on now, or gear the bridging assistant leads up to). */
function enchantTarget(view: PlayerView) {
  const st = store.enchant;
  const e = st?.mode === 'enchant' ? enchantScrollOf(view, st) : undefined;
  const target = e?.targets.find((t) => t.uid === st!.targetUid && enchantable(t));
  return st && e && target ? { st, e, target } : null;
}

/**
 * What the Enchant dialog's button sends: the enchant command (or the request to a party member), and whether
 * it lays a bridging step (the dialog then stays open for the next one).
 */
export function enchantCommand(view: PlayerView): { command: Command; bridge: boolean } | null {
  const at = enchantTarget(view);
  if (!at) return null;
  const { st, e, target } = at;
  const step = target.bridge?.steps[0];
  if (step) {
    const { opt, pick } = bridgeStep(view, step, st.bridge?.[step.rankIndex] ?? {}, !!target.blessed);
    return opt && pick ? { command: enchantSend(opt.uid, target.uid, pick), bridge: true } : null;
  }
  return { command: enchantSend(st.itemUid, target.uid, enchantPick(view, st, e, !!target.blessed)), bridge: false };
}

/**
 * The choices behind an enchanting check: who reads it (you or a party member), the enchanting powder (yours, or
 * theirs when they read it), and holy water on the gear first. `step`: a bridging step's rank index.
 */
function enchantChoices(view: PlayerView, c: EnchantChoice, p: EnchantPick, check: { skill: string; modifier: number }, water: string, step?: number): string {
  const items = view.sheet?.items ?? [];
  const at = step === undefined ? '' : ` data-ench-step="${step}"`;
  const by = view.enchanters.length ? [
    `<button type="button" class="small${p.who ? '' : ' chosen'}" data-ench-by=""${at}>You <span class="muted">${esc(check.skill)} ${signed(check.modifier)}</span></button>`,
    ...view.enchanters.map((x) => `<button type="button" class="small${p.who === x ? ' chosen' : ''}" data-ench-by="${esc(x.characterId)}"${at}${x.blocked ? ` disabled title="${esc(x.blocked)}"` : ''}>`
      + `${esc(x.name)}${x.bot ? ' <span class="tag">bot</span>' : ''} <span class="muted">${esc(check.skill)} ${signed(x.skills[check.skill] ?? 0)}</span></button>`),
  ].join('') : '';
  const powders = [
    `<button type="button" class="small${p.powder || p.theirs ? '' : ' chosen'}" data-ench-powder=""${at}>No powder</button>`,
    ...items.filter((i) => i.powder).map((i) => `<button type="button" class="small${i === p.powder ? ' chosen' : ''}" data-ench-powder="${esc(i.uid)}"${at}>${esc(i.name)}${i.qty > 1 ? ` ×${i.qty}` : ''} <span class="muted">DC –${i.powder}</span></button>`),
    p.who ? `<button type="button" class="small${p.theirs ? ' chosen' : ''}" data-ench-powder="" data-theirs${at} title="${esc(p.who.name)} picks one of their own powders (or none) when they roll">Their powder (they choose)</button>` : '',
  ].join('');
  const box = items.some((i) => i.anoint) && water
    ? `<label class="ench-water"><input type="checkbox" data-ench-water${at}${c.anoint ? ' checked' : ''}> ${water}</label>` : '';
  return `${by ? `<div class="ench-choices"><span class="lbl">Enchanter</span>${by}</div>` : ''}`
    + `<div class="ench-choices"><span class="lbl">Powder</span>${powders}</div>${box}`;
}

/** The check line: the reader's skill and modifier against the DC after powder. */
function enchantCheck(p: EnchantPick, skill: string, dc: number): string {
  return `<b>${esc(skill)} ${signed(p.modifier)}</b>${p.who ? ` <span class="muted">(${esc(p.who.name)})</span>` : ''} against <b>DC ${p.dc}</b>`
    + `${p.powder ? ` <span class="muted">(${dc} – ${p.powder.powder})</span>` : p.theirs ? ' <span class="muted">before their powder</span>' : ''}`;
}

/** A check's chance (in percent) to succeed on a first try. */
function successPct(p: { modifier: number; dc: number }): number {
  const odds = degreeOdds(p.modifier, p.dc);
  return odds[2] + odds[3];
}

/** "weapons", "shields", "armor": the gear a slot's scrolls are for. */
const KIND_NOUN: Record<string, string> = { weapon: 'weapons', shield: 'shields', armor: 'armor' };

/**
 * The bridging assistant: a rank 9–1 enchantment goes on only over the rank above it in its slot, so the slot
 * climbs one rank at a time, lowest first, with the scrolls in your pack and vault. A rail of slots, one per rank,
 * each holding the scroll it would read (your best odds, unless you swap it: click a slot for the other scrolls of
 * its rank) or blank where you have none; a blank first rank the Enchanter's Emporium could raise your enchantment
 * into offers that instead. Under it, the next rank's check: only that one can be read now.
 */
function bridgePanel(view: PlayerView, st: NonNullable<typeof store.enchant>, e: EnchantScrollView, target: EnchantScrollView['targets'][number], plan: BridgePlanView): string {
  const waiting = view.enchantRequests.find((r) => !r.incoming);
  const name = esc(target.name);
  const blessed = !!target.blessed;
  const picks = plan.steps.map((step) => {
    const c = st.bridge?.[step.rankIndex] ?? {};
    return { step, c, ...bridgeStep(view, step, c, blessed) };
  });
  const missing = picks.filter((x) => !x.opt);
  const have = plan.steps.length - missing.length;
  const goal = enchantPick(view, st, e, blessed);
  // The Emporium raises what fills the slot one rank, for gold, with no check: into the first rank if it lands there.
  const offer = view.hub?.emporium.find((o) => o.itemUid === target.uid && o.slot === plan.slot);
  const raise = offer?.increment && offer.rankIndex + 1 === plan.steps[0].rankIndex ? offer : undefined;
  const raiseButton = raise ? emporiumButton(raise, 'increment', `Raise to rank ${raise.increment!.toRank}`, raise.increment!.fee, raise.increment!.blocked) : '';
  const kind = view.hub?.enchanting?.gear.find((g) => g.uid === target.uid)?.kind;
  const chip = (x: (typeof picks)[number], n: number): string => {
    const { step, opt, pick } = x;
    const open = st.swap === step.rankIndex;
    const cls = `bridge-slot${opt ? '' : ' blank'}${n ? '' : ' next'}${open ? ' open' : ''}`;
    const rank = `<span class="bs-rank">Rank ${esc(step.rank)}</span>`;
    if (n === 0 && raise && !step.options.length) return `<span class="${cls}">${rank}<span class="bs-name muted">blank</span>${raiseButton}</span>`;
    const body = opt && pick
      ? `<span class="bs-name">${esc(opt.name)}${opt.where === 'vault' ? ' <span class="tag">vault</span>' : ''}${opt.damaged ? ' <span class="tag damaged">damaged</span>' : ''}</span><span class="bs-odds">${successPct(pick)}%</span>`
      : '<span class="bs-name muted">blank</span>';
    const title = opt ? `${opt.name}, rank ${step.rank}: click for the other rank ${step.rank} scrolls` : `You have no rank ${step.rank} ${plan.slot} scroll you can read for it`;
    return `<button type="button" class="${cls}" data-ench-swap="${step.rankIndex}" aria-expanded="${open}" title="${esc(title)}">${rank}${body}</button>`;
  };
  const rail = [
    `<span class="bridge-slot now"><span class="bs-rank">Now</span><span class="bs-name">${plan.current ? `${esc(plan.current.name)} ${esc(plan.current.rank)}` : '<span class="muted">empty</span>'}</span></span>`,
    ...picks.map(chip),
    `<span class="bridge-slot goal"><span class="bs-rank">Rank ${esc(plan.goal)}</span><span class="bs-name">★ ${esc(e.name)}</span><span class="bs-odds">${successPct(goal)}%</span></span>`,
  ].join('<span class="bridge-arrow" aria-hidden="true">→</span>');
  // The open slot's swap list: every scroll of its rank, the one it reads marked.
  const opened = picks.find((x) => x.step.rankIndex === st.swap);
  let swap = '';
  if (opened) {
    const { step, opt, c } = opened;
    const who = view.enchanters.find((x) => x.characterId === c.enchanterId);
    const options = step.options.map((o) => `<button type="button" class="small${o === opt ? ' chosen' : ''}" data-ench-scroll="${esc(o.uid)}" data-ench-step="${step.rankIndex}"${o.blocked ? ` disabled title="${esc(o.blocked)}"` : ''}>`
      + `<span class="ref"${refAttr(o.ref)}>${esc(o.name)}</span>${o.qty > 1 ? ` ×${o.qty}` : ''} <span class="tag">${o.where}</span>${o.damaged ? ' <span class="tag damaged">damaged</span>' : ''}`
      + ` <span class="muted">${esc(o.skill)} ${signed(who ? who.skills[o.skill] ?? 0 : o.modifier)} vs DC ${o.dc}</span></button>`).join('');
    const none = `No rank ${step.rank} ${plan.slot} scroll for ${kind ? KIND_NOUN[kind] : 'this gear'} in your pack or vault.`;
    swap = `<div class="bridge-swap ench-choices"><span class="lbl">Rank ${esc(step.rank)}</span>${options || `<span class="muted">${esc(none)}</span>`}${opened === picks[0] ? raiseButton : ''}</div>`;
  }
  // The next rank's check: who reads it, powder, holy water, the odds and what a failure costs.
  const { step, opt, pick: p, c } = picks[0];
  const block = !step.options.length ? 'Fill the blank rank first'
    : !opt || !p ? `You have no rank ${step.rank} scroll you can read for it`
    : waiting ? `Waiting for ${waiting.toName} to answer your request`
    : p.who ? p.who.blocked : e.blocked;
  const head = `<div class="ench-step-head"><span class="ench-rank">Rank ${esc(step.rank)}</span>${opt ? `<span class="ref"${refAttr(opt.ref)}>${esc(opt.name)}</span>` : ''}<span class="spacer"></span>`
    + `<button type="button" class="small primary" data-ench-go="bridge"${block ? ` disabled title="${esc(block)}"` : ''}>${p?.who ? 'Request' : 'Enchant'} rank ${esc(step.rank)}${plan.steps.length > 1 ? ` (1 of ${plan.steps.length})` : ''}</button></div>`;
  let card: string;
  if (!opt || !p) {
    const note = raise && !step.options.length ? `No rank ${esc(step.rank)} scroll: the Enchanter's Emporium can raise your ${esc(raise.name)} into it.`
      : `Missing: no rank ${esc(step.rank)} scroll for this slot ${step.options.length ? 'you can read now' : 'in your pack or vault'}.`;
    card = `<div class="ench-step next">${head}<span class="${raise ? 'muted' : 'bad'}">${note}</span></div>`;
  } else {
    const odds = degreeOdds(p.modifier, p.dc);
    const scroll = (crit: boolean) => (crit ? 'the scroll is destroyed' : opt.damaged ? 'the scroll crumbles away' : 'the scroll is damaged');
    const cost = (crit: boolean) => (p.blessed ? `only the blessing, and ${scroll(crit)}` : `${opt.loss * (crit ? 2 : 1)} maximum HP, and ${scroll(crit)}`);
    card = `<div class="ench-step next">${head}
      ${enchantChoices(view, c, p, { skill: opt.skill, modifier: opt.modifier }, 'Re-anoint with holy water', step.rankIndex)}
      <div class="ench-step-check">${enchantCheck(p, opt.skill, opt.dc)} <span class="muted">· ${odds[2] + odds[3]}% to succeed</span></div>
      ${oddsBar(odds)}
      <div class="ench-step-cost">Failure (${odds[1]}%) costs ${name} ${cost(false)}; critical failure (${odds[0]}%): ${cost(true)}.</div>
    </div>`;
  }
  // Every check in a row on its first try, this scroll's included.
  const chain = missing.length ? ''
    : ` All ${picks.length + 1} checks: ${Math.round(picks.reduce((all, x) => (all * successPct(x.pick!)) / 100, successPct(goal)))}% to succeed in a row.`;
  return `<h2>Bridging</h2>
    <p class="${missing.length ? 'bad' : 'good'}">You have bridges for ${have} of ${plan.steps.length} ${plan.steps.length === 1 ? 'rank' : 'ranks'}.${missing.length ? ` Missing: ${missing.map((x) => `rank ${esc(x.step.rank)}`).join(', ')}.` : ''}${chain}</p>
    <p class="muted">Each rank goes on only over the rank above it, so ${name} climbs one rank at a time; then this scroll can go on. Click a rank to read another scroll for it. The re-anoint box anoints ${name} before its rank if a failure took the blessing.</p>
    <div class="bridge-rail">${rail}</div>
    ${swap}
    <div class="ench-steps">${card}</div>`;
}

/**
 * The Enchant dialog: choose the gear an enchant scroll goes on and any enchanting powder, with the
 * DC, your chances and what a failure would cost. The Anoint dialog: choose the gear to bless.
 */
export function renderEnchant(view: PlayerView): void {
  const el = document.getElementById('enchant')!;
  const st = store.enchant;
  if (st?.helper) {
    // A scroll picked in the enchanting helper: the helper draws it (client/enchant-helper.ts).
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  const items = view.sheet?.items ?? [];
  const item = st ? items.find((i) => i.uid === st.itemUid) : undefined;
  if (!st || !item || (st.mode === 'enchant' ? !item.enchant : !item.anoint)) {
    store.enchant = null;
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const time = view.phase === 'dungeon'
    ? '<p class="muted">Takes 10 minutes: light burns for the whole party, and an encounter check is rolled.</p>'
    : '<p class="muted">In town, this takes no time at all.</p>';
  if (st.mode === 'anoint') {
    const a = item.anoint!;
    const target = a.targets.find((t) => t.uid === st.targetUid && !t.error);
    setHtml(el, `
      <div class="modal-box ench-box" role="dialog" aria-label="Anoint">
        <h1>Anoint</h1>
        <p class="muted">Sprinkle ${esc(item.name.toLowerCase())} over a piece of your gear. The next time an enchantment on it fails, it loses only the blessing. While blessed, a weapon or suit of armor wears half as fast.${view.phase === 'dungeon' && view.crawl ? ' Takes an action of dungeon time outside encounters.' : ''}</p>
        ${a.blocked ? `<p class="bad">${esc(a.blocked)}.</p>` : ''}
        <div class="ench-targets">${a.targets.map((t) => gearChoice(view, t, t.uid === target?.uid)).join('') || '<span class="muted">You have no weapons, shields or armor.</span>'}</div>
        <div class="modal-actions"><button type="button" data-ench-close>Cancel</button><button type="button" class="primary" data-ench-go ${target && !a.blocked ? '' : 'disabled'}>Anoint</button></div>
      </div>`);
    return;
  }
  const e = item.enchant!;
  const target = e.targets.find((t) => t.uid === st.targetUid && enchantable(t));
  const verb = e.rune ? 'Etch' : 'Enchant';
  const head = `
      <h1>${verb}: <span class="ref"${refAttr(e.ref)}>${esc(e.name)}</span>${e.rune ? '' : ` <span class="ench-rank">Rank ${esc(e.rank)}</span>`}`
      + `${e.damaged ? ` <span class="tag damaged" title="It survived one failure: another failure destroys it">damaged</span>` : ''}</h1>
      <p><span class="tag">${e.slot}${e.rune ? ' rune' : ''}</span> ${esc(e.text)}</p>
      ${time}
      ${e.blocked ? `<p class="bad">${esc(e.blocked)}.</p>` : ''}
      <h2>Gear</h2>
      <div class="ench-targets">${e.targets.map((t) => gearChoice(view, t, t.uid === target?.uid)).join('') || '<span class="muted">You have no weapons, shields or armor.</span>'}</div>`;
  // The dialog can run long (a bridging plan): keep its scroll position across re-renders.
  const top = el.querySelector('.modal-box')?.scrollTop ?? 0;
  const keepScroll = () => {
    const box = el.querySelector('.modal-box');
    if (box && box.scrollTop !== top) box.scrollTop = top;
  };
  setHtml(el, `
    <div class="modal-box ench-box" role="dialog" aria-label="${verb}">${head}
      ${enchantBodyHtml(view, st, e, target)}
    </div>`, keepScroll);
}

/**
 * The Enchant dialog under its Gear list, which the enchanting helper draws too: the bridging rail when the gear's
 * slot must climb first, otherwise the check (who reads it, powder, holy water), the odds and the outcomes; then the
 * buttons (in the helper, Back returns to the slot's scrolls).
 */
export function enchantBodyHtml(view: PlayerView, st: NonNullable<typeof store.enchant>, e: EnchantScrollView, target: EnchantScrollView['targets'][number] | undefined): string {
  const items = view.sheet?.items ?? [];
  const plan = target?.bridge?.steps.length ? target.bridge : null;
  const verb = e.rune ? 'Etch' : 'Enchant';
  const close = (label: string) => `<button type="button" data-ench-close>${st.helper ? 'Back' : label}</button>`;
  // A swap list for a rank the plan no longer has (laid, raised past, or no plan left) is dropped, so Esc isn't spent
  // on it and it doesn't reopen unasked if that rank comes back into the plan.
  if (st.swap !== undefined && !plan?.steps.some((s) => s.rankIndex === st.swap)) st.swap = undefined;
  if (plan) return `${bridgePanel(view, st, e, target!, plan)}<div class="modal-actions">${close('Close')}</div>`;
  const p = enchantPick(view, st, e, !!target?.blessed);
  const targetItem = target ? items.find((i) => i.uid === target.uid) : undefined;
  const [cf, f, s, cs] = degreeOdds(p.modifier, p.dc);
  const name = target ? esc(target.name) : 'the item';
  const Name = target ? name : 'The item';
  const outcome = (cls: string, label: string, pct: number, text: string) =>
    `<div class="ench-outcome ${cls}"><span class="pct">${pct}%</span><span><b>${label}</b> ${text}</span></div>`;
  const what = e.rune ? 'rune' : 'enchantment';
  const thing = e.rune ? 'rune' : 'scroll';
  const failed = e.damaged ? `The ${thing} crumbles away.` : `The ${thing} is damaged: you can try it once more.`;
  const fumbled = `The ${thing} is destroyed.`;
  const fails = p.blessed
    ? outcome('good', 'Failure', f, `The blessing takes the blow and fades; ${name} is unharmed. ${failed}`)
      + outcome('good', 'Critical Failure', cf, `The blessing takes the blow and fades; ${name} is unharmed. ${fumbled}`)
    : outcome('bad', 'Failure', f, `${Name} loses ${e.loss} maximum Hit Points for good. ${failed}`)
      + outcome('bad', 'Critical Failure', cf, `${Name} loses ${e.loss * 2} maximum Hit Points for good. ${fumbled}`);
  const d = targetItem?.durability;
  const danger = !d ? ''
    : !p.blessed && d.max - e.loss <= 0 ? `<p class="bad">A failure would destroy ${name}.</p>`
    : !p.blessed && d.max - e.loss * 2 <= 0 ? `<p class="bad">A critical failure would destroy ${name}.</p>`
    : d.broken ? `<p class="muted">${Name} is broken: its enchantments stay dormant until it's repaired${d.max <= d.bt ? ', and it has too few maximum Hit Points left to be repaired above its Broken Threshold' : ''}.</p>`
    : !p.blessed && d.hp - e.loss <= d.bt
      ? `<p class="bad">A failure would leave ${name} broken${d.max - e.loss <= d.bt ? ' for good: it could never be repaired above its Broken Threshold again' : " until it's repaired"}.</p>`
    : '';
  const ask = p.who ? `<p class="muted">${esc(p.who.name)} reads your ${thing} with their own ${esc(e.skill)}${p.who.bot ? ', at once' : ', once they accept your request'}.</p>` : '';
  const block = !target ? 'Choose a piece of gear' : target.error ?? (p.who ? p.who.blocked : e.blocked);
  return `
      <h2>Check</h2>
      ${enchantChoices(view, st, p, e, target && !target.blessed ? 'Anoint with holy water first' : '')}
      ${ask}
      <div class="ench-check">${enchantCheck(p, e.skill, e.dc)}</div>
      ${oddsBar([cf, f, s, cs])}
      ${outcome('good', 'Critical Success', cs, `The ${what} takes hold${target?.replaces ? `, replacing ${esc(target.replaces)}` : ''}, and ${name} regains up to ${e.loss} lost maximum Hit Points.`)}
      ${outcome('good', 'Success', s, `The ${what} takes hold${target?.replaces ? `, replacing ${esc(target.replaces)}` : ''}.`)}
      ${fails}
      ${danger}
      <div class="modal-actions">${close('Cancel')}<button type="button" class="primary" data-ench-go ${block ? `disabled title="${esc(block)}"` : ''}>${p.who ? 'Request' : verb}</button></div>`;
}

/** Enchanting requests between party members: the ones you were asked (roll or decline) and the ones you made (cancel). */
export function renderEnchantRequests(view: PlayerView): void {
  const el = document.getElementById('enchreq')!;
  const list = view.enchantRequests;
  for (const id of Object.keys(store.enchantAnswer)) if (!list.some((r) => r.id === id)) delete store.enchantAnswer[id];
  if (!list.length) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  setHtml(el, list.map(enchantRequestHtml).join(''));
}

function enchantRequestHtml(r: EnchantRequestView): string {
  const id = esc(r.id);
  const verb = r.rune ? 'etch' : 'read';
  const over = r.rune ? 'into' : 'over';
  const tags = `${r.blessed ? ' <span class="tag blessed">blessed</span>' : ''}${r.damaged ? ` <span class="tag damaged" title="It survived one failure: another failure destroys it">damaged</span>` : ''}`;
  if (!r.incoming) {
    return `<div class="ereq out"><span class="ereq-title">✒ Waiting for <b>${esc(r.toName)}</b> to ${verb} <b>${esc(r.scrollName)}</b> ${over} your ${esc(r.itemName)}…${tags}</span>`
      + `<span class="spacer"></span><button type="button" class="small ghost" data-ench-req="${id}" data-answer="cancel">Cancel</button></div>`;
  }
  const yours = r.powderFrom === 'enchanter' ? r.yourPowders ?? [] : [];
  const mine = yours.find((p) => p.uid === store.enchantAnswer[r.id]);
  const powder = r.powder ?? mine ?? null;
  const dc = r.dc - (powder?.value ?? 0);
  const odds = degreeOdds(r.modifier, dc);
  const picks = yours.length ? `<span class="ench-choices">`
    + `<button type="button" class="small${mine ? '' : ' chosen'}" data-ench-req="${id}" data-answer="powder" data-uid="">No powder</button>`
    + yours.map((p) => `<button type="button" class="small${p === mine ? ' chosen' : ''}" data-ench-req="${id}" data-answer="powder" data-uid="${esc(p.uid)}">${esc(p.name)}${p.qty > 1 ? ` ×${p.qty}` : ''} <span class="muted">DC –${p.value}</span></button>`).join('')
    + '</span>' : '';
  const cost = r.blessed ? 'a failure costs only the blessing' : `a failure costs it ${r.loss} maximum HP (${r.loss * 2} on a critical failure)`;
  return `<div class="ereq"><span class="ereq-title">✒ <b>${esc(r.fromName)}</b> asks you to ${verb} <b>${esc(r.scrollName)}</b> ${over} their <b>${esc(r.itemName)}</b>${tags}</span>`
    + `<span class="ereq-check"><b>${esc(r.skill)} ${signed(r.modifier)}</b> against <b>DC ${dc}</b>${powder ? ` <span class="muted">(${r.dc} – ${powder.value}, ${esc(powder.name)})</span>` : ''} · ${odds[2] + odds[3]}% to succeed</span>`
    + oddsBar(odds, ' mini')
    + picks
    + `<span class="muted">${cost}</span>`
    + (r.blocked ? `<span class="bad">${esc(r.blocked)}</span>` : '')
    + '<span class="spacer"></span>'
    + `<button type="button" class="small primary" data-ench-req="${id}" data-answer="accept"${r.blocked ? ` disabled title="${esc(r.blocked)}"` : ` title="Roll ${esc(r.skill)} against DC ${dc}"`}>Accept</button>`
    + `<button type="button" class="small ghost" data-ench-req="${id}" data-answer="decline">Decline</button></div>`;
}

/**
 * The party at the stairs down while the way down isn't settled: the clock until it presses on, who
 * chose to wait, and Wait / Press On buttons (Press On takes everyone down now, onto a floor drawn by chance).
 */
export function renderDescentHold(view: PlayerView): void {
  const el = document.getElementById('descenthold')!;
  const h = view.descentHold;
  if (!h) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const who = h.waiting.length ? `<span class="muted">Waiting: ${h.waiting.map(esc).join(', ')}</span>` : '';
  const clock = '<span class="dh-time" title="Seconds before the party presses on regardless"></span>';
  const tick = () => setText(el.querySelector('.dh-time'), `${h.secondsLeft}s`);
  setHtml(el, `<span class="dh-title">The way down is not settled yet: wait or press on.</span>${who}<span class="spacer"></span>${clock}`
    + `<button type="button" class="small ${h.youWait ? 'primary' : ''}" data-descent="wait" title="Wait for the way down to settle; the party goes down as soon as it does">${h.youWait ? 'Waiting…' : 'Wait'}</button>`
    + '<button type="button" class="small" data-descent="go" title="Take the whole party down now, onto a floor drawn by chance">Press On</button>', tick);
}

/** The party's search for firewood, above the chat bar: progress, searchers, and the next round. */
/** A vote to remove a player from the session: the tally, the clock, and Remove / Keep buttons. */
export function renderKickVote(view: PlayerView): void {
  const el = document.getElementById('kickvote')!;
  const v = view.kickVote;
  if (!v) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const tally = `<span class="kv-tally" title="Votes needed to remove: ${v.needed} of ${v.voters}">Remove ${v.yes}/${v.needed}${v.no ? ` · Keep ${v.no}` : ''}</span>`;
  // The clock ticks in place, so the Remove and Keep buttons hold still between views.
  const clock = '<span class="muted kv-time" title="Seconds left to vote"></span>';
  const tick = () => setText(el.querySelector('.kv-time'), `${v.secondsLeft}s`);
  if (v.isYou) {
    return setHtml(el, `<span class="kv-title">⚠ The party is voting on removing you from the session.</span>${tally}<span class="spacer"></span>${clock}`, tick);
  }
  const buttons = `<button type="button" class="small ${v.yourVote === 'yes' ? 'primary' : ''}" data-kickvote="yes">Remove</button>`
    + `<button type="button" class="small ${v.yourVote === 'no' ? 'primary' : ''}" data-kickvote="no">Keep</button>`;
  setHtml(el, `<span class="kv-title">${esc(v.byName)} calls a vote to remove <b>${esc(v.targetName)}</b> from the session.</span>${tally}<span class="spacer"></span>${clock}${buttons}`, tick);
}

export function renderScavenge(view: PlayerView): void {
  const el = document.getElementById('scavenge')!;
  const s = view.scavenge;
  if (!s) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const pips = Array.from({ length: s.needed }, (_, i) => `<span class="pip ${i < s.successes ? 'on' : ''}"></span>`).join('');
  const who = s.searchers.length
    ? s.searchers.map((c) => `${c.isYou ? '<b>you</b>' : esc(c.name)} <span class="muted">(Survival ${signed(c.survival)})</span>`).join(', ')
    : '<span class="muted">nobody</span>';
  const join = s.joined
    ? '<button type="button" class="small" data-scavenge="leave">Leave the Search</button>'
    : `<button type="button" class="small" data-scavenge="join"${s.joinError ? ` disabled title="${esc(s.joinError)}"` : ''}>Join the Search</button>`;
  const canSearch = !view.combat && s.searchers.length > 0;
  setHtml(el, `
    <span class="scv-title" title="${esc(s.description)}">🪵 Searching for firewood</span>
    <span class="scv-pips" title="${Math.min(s.successes, s.needed)} of ${s.needed} successes">${pips}</span>
    <span class="muted">Survival DC ${s.dc} (${esc(s.difficulty)})${s.round ? ` · round ${s.round}` : ''}</span>
    <span class="scv-who">${who}</span>
    <span class="spacer"></span>
    ${join}
    <button type="button" class="small primary" data-scavenge="search"${canSearch ? '' : ' disabled'} title="Everyone searching attempts a Survival check. Takes 10 minutes, and the party rolls 1 encounter check.">Search for 10 Minutes</button>
    <button type="button" class="small ghost" data-scavenge="stop">Call Off</button>`);
}

// ------------------------------------------------------------ character sheet

/** A round token image, or the character's initial when there's no custom token. */
export function avatar(token: string | undefined, name: string, size: 'small' | 'large'): string {
  return token
    ? `<img class="avatar ${size}" src="${esc(tokenUrl(token))}" alt="">`
    : `<span class="avatar ${size} blank">${esc(name.slice(0, 1).toUpperCase())}</span>`;
}

/** A bar's fill as a CSS width, to a tenth of a percent. */
function barWidth(value: number, max: number): string {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return `${Math.round(pct * 10) / 10}%`;
}

function bar(cls: string, value: number, max: number, label: string): string {
  return `<div class="bar ${cls}"><div style="width:${barWidth(value, max)}"></div><span>${esc(label)}</span></div>`;
}

/** What's left of a light source's fuel, in game minutes. */
function fuelText(i: ItemView): string {
  return `${Math.ceil((i.fuel ?? 0) / 60)} min`;
}

/** The dungeon-bound tag's tooltip on an item. */
const DUNGEON_BOUND_TITLE = 'Dungeon-bound: takes no gear slots, and is gone when you leave the dungeon';

/** In town before the character's first delve: selling pays full value. */
function fullRefund(view: PlayerView): boolean {
  return view.phase === 'hub' && !!view.sheet && !!view.hub && !view.hub.retrain;
}

/**
 * Drop for one item of your gear: a lone item drops at once; a stack opens a chooser to drop 1,
 * a typed amount, or all of it. The typed amount stays out of the HTML (patchSheet puts it back).
 */
function dropButtons(i: ItemView, enabled: boolean): string {
  const off = enabled ? '' : ' disabled';
  if (i.qty <= 1) return `<button class="small" data-item-act="drop" data-uid="${esc(i.uid)}"${off}>Drop</button>`;
  if (store.dropOpen !== i.uid) return `<button class="small" data-drop-open="${esc(i.uid)}"${off} title="Choose how many to drop">Drop…</button>`;
  return `<span class="drop-qty"><button class="small" data-drop="${esc(i.uid)}:1"${off}>Drop 1</button>`
    + `<input type="number" min="1" max="${i.qty}" step="1" placeholder="1–${i.qty}" data-drop-input="${esc(i.uid)}" data-keep-focus="drop" aria-label="How many to drop">`
    + `<button class="small" data-drop="${esc(i.uid)}:typed"${off} title="Drop the number typed (Enter)">Drop</button>`
    + `<button class="small" data-drop="${esc(i.uid)}:all"${off}>Drop all ${i.qty}</button>`
    + '<button class="small ghost" data-drop-close title="Cancel (Esc)">✕</button></span>';
}

/**
 * Sell, in town or to a merchant or haven trader: the first click asks ("Sell for 70 gp?" with Sell and ✕, the
 * same pattern as the Drop chooser); its Sell, or a Shift+click on the button, sells one.
 */
function sellButton(i: ItemView, how: 'town' | 'trade', price: number, title: string): string {
  const attr = how === 'town' ? 'data-sell' : 'data-trade-sell';
  if (store.sellConfirm?.uid === i.uid && store.sellConfirm.how === how) {
    return `<span class="sell-confirm"><span class="sell-ask">Sell ${i.qty > 1 ? 'one ' : ''}for ${price} gp?</span>`
      + `<button class="small danger" ${attr}="${esc(i.uid)}" data-sell-sure title="Sell it">Sell</button>`
      + '<button class="small ghost" data-sell-cancel title="Keep it (Esc)">✕</button></span>';
  }
  return `<button class="small" ${attr}="${esc(i.uid)}" title="${esc(`${title}. Shift+click sells without asking`)}">Sell ${price} gp</button>`;
}

/**
 * The price on a gear row's Sell, or null when the row has none: in town (the shop's price), or in the dungeon from
 * the pack to the merchant or haven trader in earshot (their offer). The row and `sellAskStands` both go by it.
 */
function sellPriceFor(view: PlayerView, i: ItemView, how: 'town' | 'trade'): number | null {
  if (how === 'town') return view.phase !== 'dungeon' && i.price > 0 && !i.innovation ? i.sellPrice : null;
  if (view.phase !== 'dungeon' || i.location !== 'pack') return null;
  return (view.talk?.shop?.offers[i.uid] ?? view.havenShop?.offers[i.uid]) || null;
}

/**
 * Whether a Sell question still has its row and button to draw on. One that doesn't (you left town, the merchant is
 * out of earshot, the item was sold or moved) is dropped, so it can't come back later unasked at another buyer's price.
 */
export function sellAskStands(view: PlayerView, ask: { uid: string; how: 'town' | 'trade' }): boolean {
  const i = view.sheet?.items.find((x) => x.uid === ask.uid);
  return !!i && sellPriceFor(view, i, ask.how) !== null;
}

/**
 * What a click on a Sell button does. The plain button asks (a Shift+click sells at once). The question's Sell sells,
 * but not as the second click of a double-click: the question draws where the button was, so that click was aimed at
 * the button and the player never read the question. `detail` is the click's count (0 for Enter or Space on a button).
 */
export function sellClickStep(sure: boolean, shift: boolean, detail: number): 'ask' | 'sell' | 'ignore' {
  if (sure) return detail > 1 ? 'ignore' : 'sell';
  return shift ? 'sell' : 'ask';
}

function inventory(view: PlayerView, s: CharacterSheetView): string {
  const inDungeon = view.phase === 'dungeon';
  const canAct = (id: string) => view.actions.some((a) => a.id === id && a.enabled);
  const hasAct = (id: string) => view.actions.some((a) => a.id === id);
  const items = s.items.filter((i) => i.defId !== 'gold');
  const row = (i: ItemView) => {
    // Held and worn items sit in their slot (Main hand, Off hand, Belt, Worn), which says where they are.
    const tags = [
      i.lit ? '<span class="tag lit">lit</span>' : '',
      i.temporary ? `<span class="tag temp" title="${DUNGEON_BOUND_TITLE}">dungeon-bound</span>` : '',
      i.innovation ? '<span class="tag innovation">innovation</span>' : '',
      i.holds ? `<span class="tag case" title="Enchant scrolls inside take up no gear slots">holds ${i.holds.count}/${i.holds.capacity}</span>` : '',
      i.inCase ? (i.inCase >= i.qty
        ? '<span class="tag case" title="In your scroll case: takes up no gear slots">in case</span>'
        : `<span class="tag case" title="${i.inCase} of these ride in your scroll case; the rest take up gear slots">${i.inCase} in case</span>`) : '',
      i.addsSlots ? `<span class="tag carry" title="While you carry it, you have ${i.addsSlots} more gear slots">+${i.addsSlots} slots</span>` : '',
      i.damaged ? `<span class="tag damaged" title="It survived one failure: another failure destroys it">damaged</span>` : '',
      gearTags(i),
      chamberTags(i),
      i.mode ? `<span class="tag" title="A combination weapon: its Strike uses this usage until you Switch it">${i.mode} usage</span>` : '',
      untrainedTag(i.proficiency),
      ammoTag(i),
    ].filter(Boolean).join('');
    // Fuel burns every tick: patchSheet fills it in, so the row's HTML holds still.
    const fuel = i.fuelMax ? ` <span class="muted" data-fuel="${esc(i.uid)}"></span>` : '';
    const btns: string[] = [];
    if (inDungeon) {
      // Potions and scrolls are used straight from the pack. A bomb can be drawn ahead (an Interact action), so its throw takes 1 action, not 2.
      const drawTitle = i.belt ? ' title="Hook it to your belt (an Interact action); it takes no hand"'
        : i.bomb ? ' title="Hold it in a hand (an Interact action): thrown from a hand it takes 1 action, from the pack 2"' : '';
      const carried = i.location === 'hand' || i.location === 'belt';
      if (i.location === 'pack' && (i.hands || i.belt) && !i.usable && !i.scrollAction) btns.push(`<button class="small" data-item-act="hold" data-uid="${i.uid}" ${canAct('hold') ? '' : 'disabled'}${drawTitle}>Draw</button>`);
      // A light you carry: light it, snuff it or refill it from its own row (the action acts on that item).
      if (carried && i.fuelMax && !i.lit && (i.fuel ?? 0) > 0 && hasAct('light')) btns.push(`<button class="small" data-item-act="light" data-uid="${i.uid}" ${canAct('light') ? '' : 'disabled'}>Light</button>`);
      if (carried && i.lit && hasAct('extinguish')) btns.push(`<button class="small" data-item-act="extinguish" data-uid="${i.uid}" ${canAct('extinguish') ? '' : 'disabled'}>Snuff</button>`);
      if (i.location === 'belt' && hasAct('refuel')) btns.push(`<button class="small" data-item-act="refuel" data-uid="${i.uid}" ${canAct('refuel') ? '' : 'disabled'} title="Refill it with a flask of oil">Refuel</button>`);
      if (carried) btns.push(`<button class="small" data-item-act="stow" data-uid="${i.uid}" ${canAct('stow') ? '' : 'disabled'}>Stow</button>`);
      btns.push(...gunButtons(view, i, canAct, hasAct));
      btns.push(...consumableButtons(view, i));
      btns.push(...enchantButtons(i));
      if (i.repair) {
        const why = i.repair.blocked ?? `Repair it: 10 minutes and a Crafting check (${signed(i.repair.modifier)} against DC ${i.repair.dc})`;
        btns.push(`<button class="small" data-repair="${i.uid}" ${i.repair.blocked ? 'disabled' : ''} title="${esc(why)}">Repair</button>`);
      }
      if (i.location !== 'armor') btns.push(dropButtons(i, canAct('drop')));
      const offer = sellPriceFor(view, i, 'trade');
      const buyer = view.talk?.shop ? view.talk.name : view.havenShop?.name ?? '';
      if (offer !== null) btns.push(sellButton(i, 'trade', offer, `Sell one to ${buyer}`));
      if (i.location === 'pack') btns.push(storeButton(view, i));
    } else {
      if (i.hands && i.location !== 'hand') btns.push(`<button class="small" data-equip="${i.uid}:hand0" title="Hold in your main hand">Main</button><button class="small" data-equip="${i.uid}:hand1" title="Hold in your off hand">Off</button>`);
      if (i.belt && i.location !== 'belt') btns.push(`<button class="small" data-equip="${i.uid}:belt" title="Hook it to your belt; it takes no hand">Belt</button>`);
      if (i.kind === 'armor' && i.location !== 'armor') btns.push(`<button class="small" data-equip="${i.uid}:armor">Wear</button>`);
      if (i.location !== 'pack') btns.push(`<button class="small" data-equip="${i.uid}:pack">Stow</button>`);
      if (i.canInnovate) btns.push(`<button class="small" data-innovate="${i.uid}" title="Rebuild this weapon as your weapon innovation (your current one becomes an ordinary weapon)">Make Innovation</button>`);
      btns.push(...enchantButtons(i));
      const price = sellPriceFor(view, i, 'town');
      if (price !== null) btns.push(sellButton(i, 'town', price, fullRefund(view) && i.sellPrice >= i.price ? 'Full value: until your first delve, everything you own sells for what it is worth, except what another character gave you' : 'Sells for half its value'));
      btns.push(storeButton(view, i));
    }
    // The whole label shows the item's rules block on hover; enchantment tags inside it show their own,
    // and the blessed, HP and broken tags only their title note (see rules.ts).
    // The name on its own line(s), its tags as a chip row under it, and its buttons together (style.css: gear rows).
    const buttons = btns.join('');
    return `<div class="row ${i.temporary ? 'temp' : ''}"><span class="grow"${refAttr(i.ref, i.refNote)}><span class="ref">${esc(i.name)}</span>${i.qty > 1 ? ` ×${i.qty}` : ''}${fuel}`
      + `${tags ? `<span class="gear-tags">${tags}</span>` : ''}</span>${buttons ? `<span class="gear-btns">${buttons}</span>` : ''}</div>`;
  };
  // Bags & Belts: what gives you room (a spacious pouch, a lifting belt, a scroll case), apart from the rest of the pack.
  const isBag = (i: ItemView) => !!i.addsSlots || !!i.holds;
  const packed = items.filter((i) => i.location === 'pack');
  const bags = packed.filter(isBag).map(row).join('');
  const pack = packed.filter((i) => !isBag(i)).map(row).join('');
  return `<h2>Gear <span class="muted">${s.slotsUsed}/${s.slotsMax} slots</span></h2>
    ${fullRefund(view) ? '<div class="note">Until your first delve, everything you own sells for its full value, except what another character gave you.</div>' : ''}
    ${equippedSlots(view, s, items, row)}
    ${bags ? `<div class="gear-group bags"><div class="gear-caption">Bags &amp; Belts</div>${bags}</div>` : ''}
    <div class="gear-group pack">${pack ? `<div class="gear-caption">Pack</div>${pack}` : '<span class="muted">Nothing in your pack</span>'}</div>`;
}

/**
 * Fixed slots for what you hold and wear: Main hand (Both hands for a two-handed item), Off hand,
 * Swap Hands, Belt (a lantern), then Worn. The hand holding your default Strike (first melee, main
 * hand first) says so.
 */
function equippedSlots(view: PlayerView, s: CharacterSheetView, items: ItemView[], row: (i: ItemView) => string): string {
  const main = items.find((i) => i.hand === 'main' || i.hand === 'both');
  const off = items.find((i) => i.hand === 'off');
  const belt = items.find((i) => i.location === 'belt');
  const worn = items.find((i) => i.location === 'armor');
  const slot = (label: string, item: ItemView | undefined, title = '', empty = 'Empty') =>
    `<div class="gear-slot"><span class="gear-slot-label"${title ? ` title="${esc(title)}"` : ''}>${label}</span>${item ? row(item) : `<span class="gear-empty">${empty}</span>`}</div>`;
  const twoHanded = main?.hand === 'both';
  const why = twoHanded ? `${main!.name} takes both hands`
    : !main && !off ? "You aren't holding anything"
    : view.phase === 'dungeon' && (s.dead || s.hp <= 0) ? "You can't act right now"
    : '';
  const swap = `<button type="button" class="small hand-swap" data-swap-hands${why ? ' disabled' : ''} title="${esc(why || 'Swap your main-hand and off-hand items (free). Your default Strike is your first melee Strike, main hand first.')}">⇄ Swap Hands</button>`;
  // Only the hand that actually holds the default Strike says so (a shield or a bow in the main hand defers to the other).
  const defaultHand = s.strikes.find((k) => k.default)?.hand;
  const strikeTitle = (hand: 'main' | 'off') => defaultHand === hand || (hand === 'main' && defaultHand === 'both')
    ? 'Holds your default Strike: the one you make when you attack without choosing a weapon' : '';
  return `<div class="gear-group equipped"><div class="gear-caption">Equipped</div>
    ${twoHanded ? slot('Both hands', main, strikeTitle('main')) : `${slot('Main hand', main, strikeTitle('main'))}${slot('Off hand', off, strikeTitle('off'))}`}
    <div class="hand-swap-row">${swap}</div>
    ${slot('Belt', belt, 'A lantern hangs here: it lights your way and takes no hand', 'No lantern')}
    ${slot('Worn', worn)}</div>`;
}

export const SLOT_NAMES = { prefix: 'Prefix', suffix: 'Suffix', potency: 'Potency rune', enhancement: 'Enhancement rune' } as const;

/**
 * A weapon's, shield's or armor's runes and enchantments (with their ranks), blessing and damage. Its runes share one tag
 * ("+1 · Striking", as the name has them), each part with its own rules block; the tag itself has no title, which would
 * stop the rules tooltip's search there (see rules.ts).
 */
function gearTags(i: ItemView): string {
  const all = i.enchantments ?? [];
  const runes = all.filter((e) => e.rune);
  const out = runes.length
    ? [`<span class="tag ench rune">${runes.map((e) => `<span class="ref"${refAttr(e.ref, `${SLOT_NAMES[e.slot]}: ${e.text}`)}>${esc(e.short ?? e.name)}</span>`).join(' · ')}</span>`]
    : [];
  for (const e of all.filter((x) => !x.rune)) {
    out.push(`<span class="tag ench ref"${refAttr(e.ref, `${SLOT_NAMES[e.slot]}${e.rank ? `, rank ${e.rank}` : ''}: ${e.text}`)}>${esc(e.name)}${e.rank ? ` ${esc(e.rank)}` : ''}</span>`);
  }
  // A blessed shield takes all its damage (Shield Block is its wear): only weapons and armor take half.
  if (i.blessed) out.push(`<span class="tag blessed" title="Anointed with holy water: the next failed enchantment on it harms only the blessing${i.kind === 'shield' ? '' : ', and it takes half the wear and damage'}. Falling to 0 Hit Points washes it away.">blessed</span>`);
  const d = i.durability;
  if (d && (d.hp < d.max || d.lost)) {
    const why = `Hardness ${d.hardness}; Broken Threshold ${d.bt}${d.lost ? `; ${d.lost} maximum Hit Points lost for good` : ''}`;
    out.push(`<span class="tag hp${d.hp <= d.bt ? ' low' : ''}" title="${esc(why)}">${d.hp}/${d.max} HP</span>`);
  }
  if (d?.broken) out.push('<span class="tag broken" title="At or below its Broken Threshold: it doesn\'t work, and its enchantments are dormant">broken</span>');
  return out.join('');
}

/**
 * A firearm's or crossbow's state: jammed, or not ready (unloaded, reloading 1/2); the rounds of a weapon that holds
 * several; the specialty round it fires next.
 */
function chamberTags(i: ItemView): string {
  const k = i.chamber;
  if (!k) return '';
  const out: string[] = [];
  if (k.jammed) out.push('<span class="tag broken" title="Misfired: a Reload clears the jam, then it can be reloaded">jammed</span>');
  else if (!k.ready) out.push(`<span class="tag hp low" title="${esc(k.blocked ?? 'Reload it before it can fire again')}">${k.progress ? `reloading ${k.progress.done}/${k.progress.of}` : k.loaded ? 'not ready' : 'unloaded'}</span>`);
  if (k.rounds > 1) out.push(`<span class="tag hp" title="Rounds loaded">${k.loaded}/${k.rounds} loaded</span>`);
  if (k.round) out.push(`<span class="tag ench ref"${refAttr(k.round.ref, 'Loaded: it fires with the next shot')}>${esc(k.round.name)}</span>`);
  return out.join(' ');
}

/** A specialty round: a round (firearms; some fit crossbows too) or a bolt (crossbows). */
function ammoTag(i: ItemView): string {
  const a = i.ammo;
  if (!a) return '';
  const into = a.fits === 'any' ? 'firearms and crossbows' : a.fits === 'round' ? 'firearms' : 'crossbows';
  return `<span class="tag" title="Loads into ${into}">${a.fits === 'bolt' ? 'bolt' : 'round'}</span>`;
}

/**
 * A firearm's or crossbow's buttons on its row, and a specialty round's: Reload a held gun that can't fire (in an
 * encounter; a standard round, while the bar's Reload offers the specialty ones), Unload a specialty round for a
 * standard one, Switch a combination weapon's usage, and Load a round from the pack into the held weapon its Reload
 * goes to. Unload and Load work outside encounters too (an action of dungeon time there).
 */
function gunButtons(view: PlayerView, i: ItemView, canAct: (id: string) => boolean, hasAct: (id: string) => boolean): string[] {
  const out: string[] = [];
  const k = i.chamber;
  const held = i.location === 'hand';
  const reloadWhy = () => (canAct('reload') ? '' : view.actions.find((a) => a.id === 'reload')?.reason ?? "You can't Reload right now");
  if (view.combat && held && k && !k.ready && hasAct('reload')) {
    const why = k.blocked ?? reloadWhy();
    out.push(`<button class="small" data-item-act="reload" data-uid="${i.uid}"${why ? ` disabled title="${esc(why)}"` : ' title="Reload it (an Interact action)"'}>Reload</button>`);
  }
  // A round that can't go at foes (a life shot) comes out this way too: the Strike's refusal says so.
  if (held && k?.ready && k.round && hasAct('reload')) {
    const why = reloadWhy();
    out.push(`<button class="small" data-item-act="reload" data-uid="${i.uid}" data-choice="standard"${why ? ` disabled title="${esc(why)}"` : ` title="Take out the ${esc(k.round.name.toLowerCase())} and load a standard round in its place (a Reload)"`}>Unload</button>`);
  }
  if (held && i.mode && hasAct('switch-mode')) {
    out.push(`<button class="small" data-item-act="switch-mode" data-uid="${i.uid}"${canAct('switch-mode') ? '' : ' disabled'} title="Switch it to its ${i.mode === 'melee' ? 'ranged' : 'melee'} usage (an Interact action)">Switch</button>`);
  }
  if (i.location === 'pack' && i.ammo?.into && hasAct('reload')) {
    const why = i.ammo.blocked ?? reloadWhy();
    out.push(`<button class="small" data-item-act="reload" data-uid="${i.uid}"${why ? ` disabled title="${esc(why)}"` : ` title="Load it into your ${esc(i.ammo.into)} (a Reload)"`}>Load</button>`);
  }
  return out;
}

/** Using a consumable, straight from the pack or a hand: Use (a potion), Throw (a bomb: 2 actions from the pack) or Read (a scroll). */
function consumableButtons(view: PlayerView, i: ItemView): string[] {
  const act = (id: string) => view.actions.find((a) => a.id === id);
  const off = (id: string) => (act(id)?.enabled ? '' : ` disabled title="${esc(act(id)?.reason ?? "You can't do that right now")}"`);
  // Drink it, or (main.ts useItemAt) feed it to a creature next to you.
  if (i.usable) return [`<button class="small" data-item-act="use-item" data-uid="${i.uid}"${off('use-item') || ' title="Drink it, or feed it to a creature next to you (an Interact action)"'}>Use ${glyph(1)}</button>`];
  if (i.bomb) {
    const cost = i.location === 'hand' ? 1 : 2;
    const why = act('throw-bomb')?.enabled && view.combat && cost > view.combat.actionsLeft ? "You don't have enough actions remaining" : '';
    const title = ` title="Throw it at a creature (a Strike)${cost > 1 ? '; drawing it from your pack is part of the throw' : ''}"`;
    return [`<button class="small" data-throw="${i.uid}"${why ? ` disabled title="${esc(why)}"` : off('throw-bomb') || title}>Throw ${glyph(cost)}</button>`];
  }
  const a = i.scrollAction ? act(i.scrollAction) : undefined;
  if (a) return [`<button class="small" data-act="${esc(a.id)}"${a.enabled ? ' title="Cast the spell from the scroll"' : off(a.id)}>Read ${glyph(a.cost)}</button>`];
  return [];
}

/** Your consumables in panel order: potions and elixirs, then bombs, then scrolls. */
function consumables(view: PlayerView): ItemView[] {
  const rank = (i: ItemView) => (i.usable ? 0 : i.bomb ? 1 : 2);
  return (view.sheet?.items ?? []).filter((i) => i.usable || i.bomb || !!i.scrollAction).sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** The Consumables panel (Shift+B): each consumable you carry, with its button and what it costs. */
export function consumablesHtml(view: PlayerView): string {
  const items = consumables(view);
  const rows = items.map((i) => {
    const where = i.location === 'hand' ? 'in hand' : 'pack';
    // Dungeon-bound consumables (Munition Crafter's, a relic's) stand out as they do in the gear list.
    const bound = i.temporary ? ` <span class="tag temp" title="${DUNGEON_BOUND_TITLE}">dungeon-bound</span>` : '';
    return `<div class="row ${i.temporary ? 'temp' : ''}"><span class="grow"${refAttr(i.ref, i.refNote)}><span class="ref">${esc(i.name)}</span>${i.qty > 1 ? ` ×${i.qty}` : ''} <span class="muted">${where}</span>${bound}</span>${consumableButtons(view, i).join('')}</div>`;
  }).join('');
  return `<h2>Consumables <span class="muted">${items.reduce((n, i) => n + i.qty, 0)}</span></h2>
    ${rows || '<span class="muted">You carry no potions, bombs or scrolls.</span>'}
    ${items.some((i) => i.bomb) ? '<p class="muted bag-note">A bomb thrown from your pack takes 2 actions.</p>' : ''}`;
}

/** The Inventory panel (B: all your gear) or the Consumables panel (Shift+B), over the map while it is open. */
export function renderBag(view: PlayerView): void {
  const el = document.getElementById('bag')!;
  const s = view.sheet;
  if (!store.bag || !s || view.phase !== 'dungeon') {
    store.bag = null;
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  const body = store.bag === 'all' ? inventory(view, s) : consumablesHtml(view);
  const other = store.bag === 'all'
    ? '<button type="button" class="small ghost" data-bag="consumables" title="Only your potions, bombs and scrolls (Shift+B)">Consumables</button>'
    : '<button type="button" class="small ghost" data-bag="all" title="All your gear (B)">All Gear</button>';
  setHtml(el, `<div class="bag-tools">${other}<button type="button" class="small ghost" data-bag="close" title="Close (Esc)">✕</button></div>${body}`, () => patchSheet(el, view));
}

/** The weapon's state, in a word or two, for the ammo HUD. */
function chamberWord(k: ChamberView): string {
  if (k.jammed) return 'Jammed';
  if (k.ready) return 'Ready';
  if (k.progress) return `Reloading ${k.progress.done}/${k.progress.of}`;
  return k.loaded ? 'Not ready' : 'Unloaded';
}

/** A combination weapon's usage on its ammo card: the one it's in, lit, and a button that Switches to the other. */
function usageToggle(view: PlayerView, i: ItemView): string {
  const sw = view.actions.find((a) => a.id === 'switch-mode');
  const seg = (usage: 'melee' | 'ranged', label: string) => {
    if (i.mode === usage) return `<span class="ammo-mode on">${label}</span>`;
    const why = !sw ? "You can't switch it now" : sw.enabled ? '' : sw.reason ?? "You can't switch it now";
    return `<button type="button" class="ammo-mode" data-item-act="switch-mode" data-uid="${esc(i.uid)}"`
      + `${why ? ` disabled title="${esc(why)}"` : ` title="Switch it to its ${usage} usage: an Interact action (V)"`}>${label}</button>`;
  };
  return `<div class="ammo-modes" aria-label="Usage">${seg('melee', 'Melee')}${seg('ranged', 'Ranged')}</div>`;
}

/**
 * The ammo HUD's cards: each firearm or crossbow you hold (main hand first), its rounds, and whether it can fire. A
 * combination weapon's card shows its usage too; in its melee usage the gun stays loaded for a follow-up shot, so the
 * rounds stay, dimmed ("Gun ready").
 */
export function ammoHtml(view: PlayerView): string {
  const order = { main: 0, both: 0, off: 1 } as const;
  const guns = view.phase === 'dungeon' && !view.sheet?.dead
    ? (view.sheet?.items ?? []).filter((i) => i.location === 'hand' && i.chamber).sort((a, b) => order[a.hand ?? 'main'] - order[b.hand ?? 'main'])
    : [];
  return guns.map((i) => {
    const k = i.chamber!;
    const rounds = k.rounds <= 8
      ? `<span class="ammo-pips">${Array.from({ length: k.rounds }, (_, n) => `<i class="${n < k.loaded ? 'on' : ''}"></i>`).join('')}</span>`
      : `<b class="ammo-count">${k.loaded}/${k.rounds}</b>`;
    const state = k.jammed ? 'jammed' : k.ready ? 'ready' : 'empty';
    const hand = guns.length > 1 ? `<span class="ammo-hand">${i.hand === 'off' ? 'Off hand' : 'Main hand'}</span>` : '';
    const hint = k.ready ? '' : `<kbd title="${esc(k.blocked ?? 'Reload (an Interact action)')}">R</kbd>`;
    const word = i.mode === 'melee' ? `Gun ${chamberWord(k).toLowerCase()}` : chamberWord(k);
    // The rules block hangs on the name, so hovering the usage toggle or the R doesn't cover them.
    return `<div class="ammo-card ${state}${i.mode ? ` combo ${i.mode}` : ''}">
      <div class="ammo-top"${refAttr(i.ref, i.refNote)}>${hand}<span class="ammo-name">${esc(i.name)}</span></div>
      ${i.mode ? usageToggle(view, i) : ''}
      <div class="ammo-mid">${rounds}<span class="ammo-state">${word}</span>${hint}</div>
      ${k.round ? `<div class="ammo-round">Next: ${esc(k.round.name)}</div>` : ''}
    </div>`;
  }).join('');
}

/** The ammo HUD at the bottom left of the map (see ammoHtml), hidden while you hold no firearm or crossbow. */
export function renderAmmo(view: PlayerView): void {
  const el = document.getElementById('ammo-hud')!;
  const html = ammoHtml(view);
  el.classList.toggle('hidden', !html);
  setHtml(el, html);
}

/** Enchant (an enchant scroll), Etch (a rune) and Anoint (holy water) buttons, in town and in the dungeon. */
function enchantButtons(i: ItemView): string[] {
  const out: string[] = [];
  if (i.enchant) {
    const e = i.enchant;
    const why = e.blocked ?? (e.rune
      ? `Etch the ${e.name} into a piece of your gear: ${e.skill} against DC ${e.dc}`
      : `Lay ${e.name} (rank ${e.rank}) on a piece of your gear: ${e.skill} against DC ${e.dc}`);
    out.push(`<button class="small" data-enchant="${i.uid}" ${e.blocked ? 'disabled' : ''} title="${esc(why)}">${e.rune ? 'Etch' : 'Enchant'}</button>`);
  }
  if (i.anoint) {
    const why = i.anoint.blocked ?? 'Bless a piece of your gear: it guards against its next failed enchantment and halves its wear';
    out.push(`<button class="small" data-anoint="${i.uid}" ${i.anoint.blocked ? 'disabled' : ''} title="${esc(why)}">Anoint</button>`);
  }
  return out;
}

/** The label and tags of an item lying within reach: "Torch ×3 lit". */
function pickupName(i: PickupItemView): string {
  const name = i.gold ? `${i.qty} gp` : `${esc(i.name)}${i.qty > 1 ? ` ×${i.qty}` : ''}`;
  return `<span class="ref"${refAttr(i.ref, i.refNote)}>${name}</span>${i.lit ? ' <span class="tag lit">lit</span>' : ''}${i.temporary ? ` <span class="tag temp" title="${DUNGEON_BOUND_TITLE}">dungeon-bound</span>` : ''}`;
}

/** Take 1 beside the Take button, for a stack more than one of which would fit (gold is always taken whole). */
function takeOneButton(view: PlayerView, i: PickupItemView): string {
  if (i.gold || i.qty <= 1 || i.fits <= 1) return '';
  const pick = view.actions.find((a) => a.id === 'pick-up');
  return `<button class="small ghost" data-pickup="${esc(i.uid)}:1"${pick?.enabled ? '' : ' disabled'} title="Take just one">Take 1</button>`;
}

/** The Take button for an item within reach: takes as much of the stack as fits. */
function takeButton(view: PlayerView, i: PickupItemView): string {
  const pick = view.actions.find((a) => a.id === 'pick-up');
  const why = !pick?.enabled ? pick?.reason ?? "You can't pick things up right now"
    : i.fits === 0 ? 'Not enough free gear slots'
    : i.fits < i.qty ? `Only ${i.fits} of ${i.qty} fit in your pack` : '';
  const text = i.fits > 0 && i.fits < i.qty ? `Take ${i.fits}` : 'Take';
  return `<button class="small" data-pickup="${esc(i.uid)}:${i.fits}"${pick?.enabled && i.fits > 0 ? '' : ' disabled'}${why ? ` title="${esc(why)}"` : ''}>${text}${view.combat ? ` ${glyph(1)}` : ''}</button>`;
}

/** The structures next to you, with Interact. Items within reach are in the loot panel over the map, not here. */
function nearby(view: PlayerView): string {
  const d = view.dungeon;
  const you = me(view);
  if (!d || !you) return '';
  const near = (p: { x: number; y: number }) => Math.max(Math.abs(p.x - you.x), Math.abs(p.y - you.y)) <= 1;
  const feats = d.features.filter(near).map((f) => `<div class="row"><span class="grow"><span class="ref"${refAttr(`feature:${f.kind}`)}>${esc(f.name)}</span> <span class="muted">${esc(f.state ?? '')}</span></span>${f.interactive ? `<button class="small" data-feature="${f.x},${f.y}">Interact</button>` : ''}</div>`);
  if (!feats.length) return '';
  return `<h2>Nearby</h2><div class="list">${feats.join('')}</div>`;
}

/** The loot panel: hidden (nothing within reach, you're down, or in town), collapsed to its bar, or open. */
export function lootState(view: PlayerView): 'open' | 'collapsed' | null {
  const p = view.pickup;
  const s = view.sheet;
  if (view.phase !== 'dungeon' || !p?.squares.length || !s || s.dead || s.hp <= 0) return null;
  return p.squares.every((sq) => sq.items.every((i) => store.lootDismissed.has(i.uid))) ? 'collapsed' : 'open';
}

/** Collapses the loot panel for what lies within reach now (it opens again for anything new), or opens it. */
export function setLootCollapsed(view: PlayerView, collapsed: boolean): void {
  for (const sq of view.pickup?.squares ?? []) {
    for (const i of sq.items) {
      if (collapsed) store.lootDismissed.add(i.uid);
      else store.lootDismissed.delete(i.uid);
    }
  }
}

/**
 * The loot panel over the map (see lootState): when collapsed, one bar that counts the items within reach; open,
 * everything within reach (your square and the adjacent ones) by square, the one clicked first, with what fits in
 * your pack. It holds no ticking values, so it is rewritten only when what lies within reach changes.
 */
export function pickupHtml(view: PlayerView, state = lootState(view)): string {
  const p = view.pickup;
  if (!state || !p) return '';
  const count = p.squares.reduce((n, s) => n + s.items.length, 0);
  const items = `${count} ${count === 1 ? 'item' : 'items'} within reach`;
  if (state === 'collapsed') {
    return `<button type="button" class="pk-head pk-bar" data-loot-toggle title="Show what lies within reach (G)"><b>Within Reach</b><span class="muted">${items}</span><span class="pk-caret">▸</span></button>`;
  }
  const focus = store.pickupFocus;
  const isFocus = (sq: PickupSquareView) => !!focus && sq.x === focus.x && sq.y === focus.y;
  const squares = [...p.squares].sort((a, b) => Number(isFocus(b)) - Number(isFocus(a)));
  const rows = squares.map((sq) => {
    const head = `<div class="pk-where"><b>${esc(sq.where)}</b>${sq.feature ? ` <span class="muted">· on the ${esc(sq.feature.name.toLowerCase())}</span>` : ''}`
      + `${sq.feature?.interactive ? `<button class="small ghost" data-feature="${sq.x},${sq.y}" title="Use the ${esc(sq.feature.name.toLowerCase())} instead">Interact</button>` : ''}</div>`;
    const list = sq.items.map((i) => {
      const slots = i.slots === 0 ? 'no slots' : `${i.slots} slot${i.slots === 1 ? '' : 's'}`;
      return `<div class="pk-item"><span class="pk-glyph" style="color:${esc(i.color)}">${esc(i.glyph)}</span><span class="grow">${pickupName(i)} <span class="muted pk-slots">${slots}</span></span>${takeOneButton(view, i)}${takeButton(view, i)}</div>`;
    }).join('');
    return `<div class="pk-square${isFocus(sq) ? ' focus' : ''}">${head}${list}</div>`;
  }).join('');
  const footer = p.canTakeAll
    ? '<button class="small primary" data-pickup-all>Take Everything</button><span class="muted">or press <kbd>G</kbd></span>'
    : view.combat ? `<span class="muted">Each item takes an Interact action ${glyph(1)} to pick up.</span>` : '';
  return `
    <div class="pk-head"><b>Within Reach</b><span class="muted">${items} · gear ${p.slotsUsed}/${p.slotsMax} slots</span><button type="button" class="small ghost" data-loot-toggle title="Collapse (Esc): it opens again when something new comes within reach">▾</button></div>
    <div class="pk-list">${rows}</div>
    ${footer ? `<div class="pk-foot">${footer}</div>` : ''}`;
}

/**
 * The loot panel, top left of the map: open by itself whenever items lie within reach (in encounters too), until you
 * collapse it (see lootState). The square clicked to point it at stays first while it is within reach.
 */
export function renderPickup(view: PlayerView): void {
  const el = document.getElementById('pickup')!;
  const state = lootState(view);
  const f = store.pickupFocus;
  if (f && !view.pickup?.squares.some((sq) => sq.x === f.x && sq.y === f.y)) store.pickupFocus = null;
  el.classList.toggle('hidden', !state);
  el.classList.toggle('collapsed', state === 'collapsed');
  keepScroll(el, ['.pk-list'], () => setHtml(el, pickupHtml(view, state)));
}

export function renderSheet(view: PlayerView): void {
  const el = document.getElementById('sheet')!;
  setHtml(el, sheetHtml(view), () => patchSheet(el, view));
}

/**
 * Fills in the sheet's values that change every tick while a light burns: the light bar and each
 * light source's fuel. Writes only what changed, so hovered and pressed elements stay put.
 */
function patchSheet(el: Element, view: PlayerView): void {
  const s = view.sheet;
  if (!s) return;
  const light = el.querySelector<HTMLElement>('[data-light-bar]');
  if (light && s.light) {
    const fill = light.firstElementChild as HTMLElement | null;
    const width = barWidth(s.light.fuel, s.light.fuelMax);
    if (fill && fill.style.width !== width) fill.style.width = width;
    setText(light.lastElementChild, `${s.light.name} ${s.light.lit ? `· ${realTimeLeft(view, s.light.fuel)} remaining` : '· unlit'}`);
  }
  for (const f of el.querySelectorAll<HTMLElement>('[data-fuel]')) {
    const item = s.items.find((i) => i.uid === f.dataset.fuel);
    if (item) setText(f, fuelText(item));
  }
  // The Drop chooser was rewritten under you: put back what you typed.
  const typed = el.querySelector<HTMLInputElement>('[data-drop-input]');
  if (typed && !typed.value && store.dropTyped) typed.value = store.dropTyped;
}

/**
 * The character sheet's HTML. Burning fuel is left out (patchSheet fills it in), so the HTML stays
 * the same from tick to tick and setHtml doesn't replace the panel under the pointer.
 */
export function sheetHtml(view: PlayerView): string {
  const s = view.sheet;
  if (!s) return '';
  const p = view.pending;
  const feats = p?.hands.filter((h) => h.kind !== 'spell').length ?? 0;
  const spells = p?.hands.filter((h) => h.kind === 'spell').length ?? 0;
  const choices = [
    feats ? `select ${feats} feat${feats > 1 ? 's' : ''}` : '',
    spells ? `learn ${spells} spell${spells > 1 ? 's' : ''}` : '',
  ].filter(Boolean);
  const pendingBadge = [
    choices.length ? `<button class="pending-badge" data-open-modal>★ ${esc(choices.join(', ').replace(/^./, (c) => c.toUpperCase()))}</button>` : '',
    p?.boostsLeft ? `<button class="pending-badge" data-open-boosts>★ Apply ${p.boostsLeft} attribute boost${p.boostsLeft > 1 ? 's' : ''}</button>` : '',
  ].join('');
  const attrs = (Object.keys(s.attributes) as (keyof typeof s.attributes)[]).map((a) => {
    const partial = s.partialBoosts.includes(a);
    return `<div class="attr ${partial ? 'partial' : ''}" title="${partial ? 'Partial boost: one more boost increases this modifier by 1' : ''}"><span class="muted">${a.toUpperCase()}</span><br><b>${signed(s.attributes[a])}${partial ? '½' : ''}</b></div>`;
  }).join('');
  const light = s.light
    ? '<div class="bar light" data-light-bar><div></div><span></span></div>'
    : '<div class="muted">You aren\'t holding a light source. You can perceive only adjacent squares.</div>';
  const conditions = s.conditions.map((c) => `<span class="tag cond"${refAttr(c.ref)}>${esc(c.name.toLowerCase())}${c.valued ? ` ${c.value}` : ''}</span>`).join(' ');
  const effects = s.effects.map((e) => {
    const kind = e.kind === 'curse' ? 'curse' : e.kind === 'boon' ? 'boon' : 'effect';
    return `<div class="row ${kind}"${refAttr(e.ref)}><span class="grow">${esc(e.name)} <span class="tag ${kind}">${kind}</span><div class="desc">${esc(e.description)}</div></span></div>`;
  }).join('');
  // A gun that can't fire now greys its Strike, saying why (unloaded, jammed, the wrong round, a hand busy).
  const strikes = s.strikes.map((k) => `<div class="row"><span class="grow"><b class="ref"${refAttr(k.ref)}>${esc(k.name)}</b>${defaultTag(k)}${unreadyTag(k)} ${signed(k.attack)} <span class="muted">${esc(k.damage)}${k.traits.length ? ` · ${esc(k.traits.join(', '))}` : ''}</span></span>${view.combat ? `<button class="small" data-strike="${esc(k.key)}"${k.unready ? ` disabled title="${esc(k.unready)}"` : ''}>Strike</button>` : ''}</div>`).join('');
  const resources = s.resources.map((r) => `<div class="row"><span class="grow">${esc(r.name)}</span><b>${r.value}/${r.max}</b></div>`).join('');
  const reactions = s.reactions.map((r) => `<div class="row"><span class="grow ref"${refAttr(r.ref)}>${glyph('reaction')} ${esc(r.name)}</span><button class="small ${r.enabled ? 'on' : ''}" data-reaction="${esc(r.id)}:${!r.enabled}" title="${r.enabled ? 'Used automatically when triggered. Click to turn off.' : 'Turned off. Click to use automatically.'}">${r.enabled ? 'Auto' : 'Off'}</button></div>`).join('');
  const featRow = (f: CharacterSheetView['feats'][number]) => `<div class="row ${f.temporary ? 'temp' : ''}"${refAttr(f.ref)}><span class="grow">${esc(f.name)} <span class="muted">${f.level}</span>${f.temporary ? ' <span class="tag temp">dungeon-bound</span>' : ''}${f.flexible ? ' <span class="tag" title="Combat Flexibility: until your next daily preparations">flexible</span>' : ''}</span></div>`;
  const featGroups: [string, string][] = [['ancestry-feat', 'Ancestry'], ['class-feat', 'Class'], ['skill-feat', 'Skill'], ['general-feat', 'General']];
  const slotFeats = (key: string | undefined) => s.feats.filter((f) => (key ? f.slot === key : !f.slot || !featGroups.some(([k]) => k === f.slot)));
  const featRows = [...featGroups.map(([k, label]) => [label, slotFeats(k)] as const), ['Granted', slotFeats(undefined)] as const]
    .filter(([, list]) => list.length)
    .map(([label, list]) => `<div class="gear-caption">${label}</div>${list.map(featRow).join('')}`).join('');
  const boostRows = s.boostHistory.filter((b) => b.attributes.length).map((b) => `<div class="row"><span class="grow muted">Level ${b.level} · ${esc(b.source)}</span><span>${b.attributes.map((a) => a.toUpperCase()).join(' ')}</span></div>`).join('');
  const features = s.features.map((f) => `<div class="row"${refAttr(f.ref)}><span class="grow">${esc(f.name)} <span class="muted">${f.level}</span></span></div>`).join('');
  const gold =view.phase === 'dungeon' ? `${s.gold} gp kept · <span class="tone-loot">${s.runGold} gp found on this delve</span>` : `${s.gold} gp`;

  return `
    <div class="section">
      <div class="sheet-head"><button class="token-btn" data-token-edit="${esc(s.id)}" title="Change your token">${avatar(s.token, s.name, 'large')}</button><span class="name">${esc(s.name)}</span><span class="muted">${esc(s.ancestry)} ${esc(s.className)} ${s.level}${s.keyAttribute ? ` · ${esc(s.keyAttribute)}` : ''}${s.background ? ` · ${esc(s.background.name)}` : ''}</span></div>
      ${s.dead ? '<div class="bad">This character is dead.</div>' : ''}
      ${pendingBadge}
      ${bar('hp', s.hp, s.maxHp, `HP ${s.hp}/${s.maxHp}${s.tempHp ? ` (+${s.tempHp} temporary)` : ''}`)}
      ${bar('xp', s.xp, s.xpToNext, `XP ${s.xp}/${s.xpToNext}`)}
      ${light}
      <div class="conds">${conditions}</div>
    </div>
    <div class="stats">
      <div class="stat"><b>${s.ac}</b><span>AC</span></div>
      <div class="stat"><b>${signed(s.fort)}</b><span>Fort</span></div>
      <div class="stat"><b>${signed(s.ref)}</b><span>Ref</span></div>
      <div class="stat"><b>${signed(s.will)}</b><span>Will</span></div>
      <div class="stat"><b>${signed(s.perception)}</b><span>Perception</span></div>
      <div class="stat"><b>${s.speed}</b><span>Speed</span></div>
      <div class="stat"><b>${s.classDc}</b><span>Class DC</span></div>
      <div class="stat"><b>${s.gold}</b><span>gp</span></div>
    </div>
    <h2>Attributes</h2><div class="attrs">${attrs}</div>
    <div class="muted" style="margin-top:6px">${gold}</div>
    ${boostRows ? `<div class="gear-caption">Attribute boosts</div><div class="list boosts">${boostRows}</div>` : ''}
    ${nearby(view)}
    ${effects ? `<h2>Effects</h2><div class="list">${effects}</div>` : ''}
    <h2>Strikes</h2><div class="list">${strikes || '<span class="muted">No Strikes available</span>'}</div>
    ${spellsSection(view, s)}
    ${resources ? `<h2>Resources</h2><div class="list">${resources}</div>` : ''}
    ${reactions ? `<h2>Reactions</h2><div class="list">${reactions}</div>` : ''}
    ${debilitationsHtml(s)}
    ${constructHtml(s)}
    ${inventory(view, s)}
    <h2>Feats</h2><div class="list">${featRows || '<span class="muted">None</span>'}</div>
    <h2>Class Features</h2><div class="list">${features}</div>
    ${proficienciesHtml(s)}
  `;
}

/** What each proficiency rank adds to a check or DC, for the rank's tooltip. */
const RANK_BONUS: Record<string, string> = {
  untrained: 'Untrained: adds no proficiency bonus',
  trained: 'Trained: adds your level + 2',
  expert: 'Expert: adds your level + 4',
  master: 'Master: adds your level + 6',
  legendary: 'Legendary: adds your level + 8',
};

/**
 * Your proficiency ranks: weapons and armor (what you can use well), Perception, saves and DCs, then every skill with
 * its modifier. Untrained ones are dimmed.
 */
function proficienciesHtml(s: CharacterSheetView): string {
  const cell = (name: string, rank: string, mod?: number) => `<div class="prof rank-${esc(rank)}" title="${esc(RANK_BONUS[rank] ?? rank)}">`
    + `<span class="prof-name">${esc(name)}${mod === undefined ? '' : ` <span class="muted">${signed(mod)}</span>`}</span><span class="prof-rank">${esc(rank)}</span></div>`;
  const group = (caption: string, cells: string[]) => (cells.length ? `<div class="gear-caption">${caption}</div><div class="prof-grid">${cells.join('')}</div>` : '');
  const of = (g: CharacterSheetView['proficiencies'][number]['group']) => s.proficiencies.filter((p) => p.group === g).map((p) => cell(p.name, p.rank));
  return `<h2>Proficiencies</h2>
    ${group('Weapons', of('weapons'))}
    ${group('Armor', of('armor'))}
    ${group('Perception, Saves and DCs', of('defenses'))}
    ${group('Skills', s.skills.map((k) => cell(k.name, k.rank, k.mod)))}`;
}

/** A weapon or suit of armor you're untrained with gets a warning tag, saying what it costs you (none when you're trained). */
function untrainedTag(p: ItemProficiencyView | undefined): string {
  if (p?.rank !== 'untrained') return '';
  const why = p.what.endsWith('armor')
    ? `You're untrained in ${p.what}: worn, it adds no proficiency bonus to your AC`
    : `You're untrained with ${p.what}: you add no proficiency bonus to attack with it`;
  return ` <span class="tag broken" title="${esc(why)}">untrained</span>`;
}

/** An inventor's construct companion: its token (click to change it) and its name (Rename). */
function constructHtml(s: CharacterSheetView): string {
  const k = s.construct;
  if (!k) return '';
  return `<h2>Construct Companion</h2>
    <div class="construct-head"><button class="token-btn" data-token-edit="${esc(s.id)}" data-token-construct title="Change your construct's token">${avatar(k.token, k.name, 'large')}</button><span class="name">${esc(k.name)}</span><button type="button" data-construct-name title="Give your construct a name">${k.named ? 'Rename' : 'Name It'}</button></div>
    <div class="note muted">It acts only when you Command it. Anyone can talk to it (Alt+click it): it answers, in its own way.</div>`;
}

/**
 * Debilitating Strike (a rogue from 9th level): Auto, or your debilitations in the order you clicked them
 * (click one again to drop it), and with Master Strike whether it comes first. The HTML changes only when the
 * options or your preferences do.
 */
function debilitationsHtml(s: CharacterSheetView): string {
  const deb = s.debilitations;
  if (!deb) return '';
  const options = deb.options.map((o) => {
    const n = deb.order.indexOf(o.id) + 1;
    return `<button class="small ${n ? 'on' : ''}" data-debilitation="${esc(o.id)}" title="${esc(o.description)}">${n ? `${n}. ` : ''}${esc(o.name)}${o.critOnly ? ' (critical hits)' : ''}</button>`;
  }).join('');
  const master = deb.masterStrike === undefined ? ''
    : `<button class="small ${deb.masterStrike ? 'on' : ''}" data-master-strike="${!deb.masterStrike}" title="Use Master Strike before Debilitating Strike against creatures not yet immune to it.">Master Strike: ${deb.masterStrike ? 'Auto' : 'Off'}</button>`;
  return `<h2 class="ref"${refAttr('classfeature:rogue:debilitating-strike')}>Debilitating Strike</h2>
    <div class="note">${deb.slots > 1 ? 'Two debilitations per hit: ' : 'One debilitation per hit: '}taken in your order when they can work on the target; Auto picks the rest.</div>
    <div class="row wrap" data-debilitations>
      <button class="small ${deb.order.length ? '' : 'on'}" data-debilitation="auto" title="Picks the most useful debilitation for each hit.">Auto</button>${options}${master}
    </div>`;
}

/**
 * Your Debilitating Strike order after clicking a button of the block above: Auto clears it, a debilitation in it
 * leaves it (the ones after it move up), any other joins the end.
 */
export function nextDebilitationOrder(order: readonly string[], id: string): string[] {
  if (id === 'auto') return [];
  return order.includes(id) ? order.filter((x) => x !== id) : [...order, id];
}

const HAND_LABEL: Record<HandSlot, string> = { main: 'main hand', off: 'off hand', both: 'both hands' };

/** Marks the Strike you make when you attack without choosing one (map clicks, the Strike button). */
function defaultTag(k: StrikeView): string {
  if (!k.default) return '';
  const why = `Your default Strike${k.hand ? ` (${HAND_LABEL[k.hand]})` : ''}: the one you make when you attack without choosing a weapon`;
  return ` <span class="tag default" title="${esc(why)}">default</span>`;
}

/**
 * A gun's Strike that can't fire now: jammed, unloaded, not ready (rounds still in it: a chamber to turn, a magazine to
 * cock), or loaded but held back (a round for allies or squares, a hand that must be free).
 */
function unreadyTag(k: StrikeView): string {
  if (!k.unready) return '';
  const word = k.jammed ? 'jammed' : k.cure === 'free-hand' ? 'needs a free hand' : k.cure === 'standard-round' ? 'not at foes' : k.chambered ? 'not ready' : 'unloaded';
  return ` <span class="tag ${k.jammed ? 'broken' : 'hp low'}" title="${esc(k.unready)}">${word}</span>`;
}

function spellsSection(view: PlayerView, s: CharacterSheetView): string {
  const sc = s.spellcasting;
  if (!sc) return '';
  const rows = sc.spells.map((sp) => {
    const action = view.actions.find((a) => a.id === `spell:${sp.id}`);
    const button = view.phase === 'dungeon' ? `<button class="small" data-act="spell:${esc(sp.id)}" ${action?.enabled ? '' : 'disabled'} title="${esc(action?.reason ?? '')}">Cast</button>` : '';
    return `<div class="row ${sp.lost ? 'lost' : ''} ${sp.temporary ? 'temp' : ''}"${refAttr(sp.ref)}>
      <span class="grow"><b>${esc(sp.name)}</b> ${glyph(sp.cost)} <span class="muted">${sp.rank === 0 ? 'Cantrip' : `Spell ${sp.rank}`} · casting DC ${sp.castDc}</span>
      ${sp.lost ? '<span class="tag lost">lost until long rest</span>' : ''}${sp.temporary ? ' <span class="tag temp">dungeon-bound</span>' : ''}</span>${button}</div>`;
  }).join('');
  return `<h2>Spells <span class="muted">${esc(sc.tradition)} · spellcasting check ${signed(sc.check)} · spell attack ${signed(sc.attack)} · spell DC ${sc.dc}</span></h2>
    <div class="note">To cast a spell, attempt a spellcasting check against its casting DC. A critical success is just a success. On a failure, the spell has no effect, and you can't cast it again until your next long rest (Make Camp). On a critical failure, you also roll 1d12 on the spell mishap table.</div>
    <div class="list">${rows || '<span class="muted">No spells known</span>'}</div>`;
}

// ------------------------------------------------------------ hub

/** Re-delving: start on any floor down to the deepest one a party member has reached. */
function delvePicker(delve: { depth: number; max: number }): string {
  if (delve.max <= 1) return '';
  const options = Array.from({ length: delve.max }, (_, i) => i + 1)
    .map((n) => `<option value="${n}" ${n === delve.depth ? 'selected' : ''}>Floor ${n}${n === delve.max ? ' (deepest reached)' : ''}</option>`).join('');
  return `<div class="hub-top delve">
    <label>Start the delve on <select data-delve aria-label="Starting floor">${options}</select></label>
    <span class="muted">Each floor is balanced for characters of its level: floor ${delve.depth} for level ${Math.min(20, delve.depth)}. Changing it asks everyone to ready up again.</span>
  </div>`;
}

/** A Buy button: disabled, with the reason as its title, when you can't buy the item now. */
function buyButton(id: string, blocked: string | undefined, ware?: number, emporium = false): string {
  return `<button class="small" data-buy="${esc(id)}"${ware === undefined ? '' : ` data-ware="${ware}"`}${emporium ? ' data-buy-emporium="1"' : ''}${blocked ? ` disabled title="${esc(blocked)}"` : ''}>Buy</button>`;
}

/** A special ware's card (or, with `emporium`, one of the Enchanter's Emporium's scrolls). */
function wareCard(w: SpecialWareView, emporium = false): string {
  return `<div class="special-card${w.sold ? ' sold' : ''}">
      <div class="sc-name"><span class="ref"${refAttr(w.ref, w.refNote)}>${esc(w.name)}</span> ${levelTag(w.level)}${untrainedTag(w.proficiency)}</div>
      ${w.summary ? `<div class="sc-summary">${esc(w.summary)}</div>` : ''}
      ${w.detail ? `<div class="sc-detail">${esc(w.detail)}</div>` : ''}
      <div class="sc-foot"><span class="tone-loot">${w.price} gp</span>${w.sold ? '<span class="tag sold">Sold</span>' : buyButton(w.id, w.blocked, w.ware, emporium)}</div>
    </div>`;
}

/** "Item 3" for magic and alchemical items (mundane gear has no level). */
function levelTag(level: number): string {
  return level > 0 ? `<span class="tag level">Item ${level}</span>` : '';
}

/** Your special wares: cards of items from level 1 up to yours, each sold once until you next return from the dungeon. */
function specialsHtml(specials: NonNullable<PlayerView['hub']>['specials']): string {
  if (!specials) return '';
  const card = (w: SpecialWareView) => wareCard(w);
  return `<section class="specials">
    <div class="specials-head"><b>Special Stock (${specials.level > 1 ? `Levels 1–${specials.level}` : 'Level 1'})</b> <span class="muted">Items from level 1 up to yours; each sells once. The stock changes each time you return from the dungeon.</span></div>
    <div class="specials-grid">${specials.wares.map(card).join('') || '<span class="muted">Nothing up to your level is in stock.</span>'}</div>
  </section>`;
}

/**
 * The regular stock: a tab per section (All shows every section under its heading), then one
 * compact row per item. The full rules are in each name's tooltip.
 */
function shopListHtml(view: PlayerView, hub: NonNullable<PlayerView['hub']>): string {
  const owned = new Map<string, number>();
  for (const i of view.sheet?.items ?? []) owned.set(i.defId, (owned.get(i.defId) ?? 0) + i.qty);
  // Sections with nothing in them get no tab; a remembered tab that no longer exists falls back to All.
  const counts = hub.categories.map((k) => ({ ...k, count: hub.shop.filter((i) => i.category === k.id).length })).filter((k) => k.count);
  const tab = store.shopTab === 'emporium' || counts.some((k) => k.id === store.shopTab) ? store.shopTab : 'all';
  const tabButton = (id: string, name: string, count: number) =>
    `<button type="button" class="shop-tab${tab === id ? ' on' : ''}" data-shop-tab="${esc(id)}" aria-pressed="${tab === id}">${esc(name)} <span class="count">${count}</span></button>`;
  const tabs = [
    tabButton('all', 'All', hub.shop.length), ...counts.map((k) => tabButton(k.id, k.name, k.count)), tabButton('emporium', "Enchanter's Emporium", hub.emporium.length + hub.emporiumWares.length),
  ].join('');
  if (tab === 'emporium') return `<div class="shop-tabs" role="toolbar" aria-label="Shop sections">${tabs}</div><div class="shop-list">${emporiumHtml(hub.emporium, hub.emporiumWares, false, !!hub.enchanting)}</div>`;
  const row = (i: ShopItemView) => {
    const n = owned.get(i.id);
    return `<div class="shop-row">
      <span class="sr-name"><span class="ref"${refAttr(i.ref, i.refNote)}>${esc(i.name)}</span>${untrainedTag(i.proficiency)}</span>
      <span class="sr-level">${levelTag(i.level)}</span>
      <span class="sr-summary" title="${esc(i.summary ?? '')}">${esc(i.summary ?? '')}</span>
      <span class="sr-owned">${n ? `<span class="tag owned" title="You carry ${n}">×${n}</span>` : ''}</span>
      <span class="sr-price tone-loot">${i.price} gp</span>
      ${buyButton(i.id, i.blocked)}
    </div>`;
  };
  const sections = hub.categories.filter((k) => tab === 'all' || k.id === tab).map((k) => {
    const items = hub.shop.filter((i) => i.category === k.id);
    if (!items.length) return '';
    return `<div class="shop-section">${tab === 'all' ? `<div class="gear-caption">${esc(k.name)}</div>` : ''}${items.map(row).join('')}</div>`;
  }).join('');
  return `<div class="shop-tabs" role="toolbar" aria-label="Shop sections">${tabs}</div><div class="shop-list">${sections}${tab === 'all' ? emporiumHtml(hub.emporium, hub.emporiumWares, true, !!hub.enchanting) : ''}</div>`;
}

/** An Enchanter's Emporium button for an enchantment on your gear: raise it a rank or extract it, for its fee (greyed out, with why, when it can't). */
export function emporiumButton(o: EmporiumOfferView, action: 'increment' | 'extract', label: string, fee: number, blocked?: string): string {
  return `<button class="small" data-emporium="${action}" data-emporium-item="${esc(o.itemUid)}" data-emporium-slot="${o.slot}"${blocked ? ` disabled title="${esc(blocked)}"` : ''}>${esc(label)} · ${fee} gp</button>`;
}

/** The button that opens the town's enchanting helper (client/enchant-helper.ts). */
const ENCHANT_HELPER_BUTTON = '<button type="button" class="small" data-ench-helper="open" title="Your gear\'s prefixes and suffixes, and every enchant scroll that could go there">✦ Enchanting</button>';

/**
 * The Enchanter's Emporium: its enchant scrolls for sale (random lines and ranks, at a markup), then
 * each prefix and suffix on your gear, to raise one rank or pull out into a scroll, for gold.
 */
function emporiumHtml(offers: NonNullable<PlayerView['hub']>['emporium'], wares: SpecialWareView[], caption: boolean, helper = false): string {
  const button = emporiumButton;
  const row = (o: (typeof offers)[number]) => `<div class="shop-row emporium-row">
      <span class="sr-name" title="${esc(o.itemName)}">${esc(o.itemName)}</span>
      <span class="er-slot"><span class="tag">${SLOT_NAMES[o.slot]}</span></span>
      <span class="er-ench"><span class="tag ench ref"${refAttr(o.ref, `${SLOT_NAMES[o.slot]}, rank ${o.rank}: ${o.text}`)}>${esc(o.name)} ${esc(o.rank)}</span></span>
      <span class="er-actions">${o.increment
        ? button(o, 'increment', `Raise to rank ${o.increment.toRank}`, o.increment.fee, o.increment.blocked)
        : `<span class="muted" title="${esc(o.name)} comes in no higher rank">Top rank</span>`}${button(o, 'extract', 'Extract', o.extract.fee, o.extract.blocked)}</span>
    </div>`;
  return `<div class="shop-section emporium">
    ${caption ? `<div class="gear-caption">Enchanter's Emporium</div>` : ''}
    ${wares.length ? `<div class="er-note muted">Enchant scrolls at random ranks, at a steep markup; each sells once. New scrolls each time you return from the dungeon.</div>
    <div class="specials-grid">${wares.map((w) => wareCard(w, true)).join('')}</div>` : ''}
    <div class="er-note muted">Raising an enchantment one rank skips bridging and the check. Extracting one empties its slot and gives you its scroll, which needs bridging again to go on other gear above rank A.</div>
    ${helper ? `<div class="er-note">${ENCHANT_HELPER_BUTTON} <span class="muted">Every scroll in your pack and vault that fits your gear, and the bridging each needs.</span></div>` : ''}
    ${offers.map(row).join('') || '<span class="muted">None of your gear bears an enchantment.</span>'}
  </div>`;
}

/** Retraining (after the first delve): swap a feat, skill increase or spell you chose, once per return, for gold. */
function retrainHtml(view: PlayerView): string {
  const r = view.hub?.retrain;
  if (!r) return '';
  const sel = store.retrain && r.targets.some((t) => t.kind === store.retrain!.kind && t.id === store.retrain!.remove) ? store.retrain : null;
  const targets = r.targets.map((t) => {
    const why = t.locked ?? r.blocker ?? '';
    const on = sel && sel.kind === t.kind && sel.remove === t.id;
    const kind = t.kind === 'spell' ? 'Spell' : t.kind === 'skill-increase' ? 'Skill increase' : `${(t.slot ?? 'feat').replace('-feat', '')} feat`;
    return `<div class="row ${on ? 'selected' : ''}"><span class="grow"><b>${esc(t.name)}</b> <span class="muted">${esc(kind)} · level ${t.level}${t.locked ? ` · ${esc(t.locked)}` : ''}</span></span><button class="small" ${why ? 'disabled' : ''} ${why ? `title="${esc(why)}"` : ''} data-retrain-target="${esc(t.kind)}|${esc(t.id)}">${on ? 'Choosing…' : 'Retrain'}</button></div>`;
  }).join('');
  let options = '';
  if (sel) {
    const o = store.retrainOptions && store.retrainOptions.kind === sel.kind && store.retrainOptions.remove === sel.remove ? store.retrainOptions : null;
    if (!o) options = '<p class="muted">Looking for choices…</p>';
    else {
      const opts: PickOpt[] = o.options;
      const cfg: ListCfg = { spell: sel.kind === 'spell', codex: store.welcome?.codex ?? null, mode: 'focus', attr: 'retrain-take', value: (id) => id, takeLabel: `Retrain for ${r.cost} gp` };
      options = `<div class="retrain-options"><p class="muted">Choose what to take instead. It must be something that choice could have been at its level.</p>
        <div class="pl" data-pl-box="retrain">${controlsHtml(opts, store.retrainList, cfg, 'retrain')}<div class="pl-list">${listHtml(opts, store.retrainList, cfg)}</div></div>
        <button class="small ghost" data-retrain-cancel>Cancel</button></div>`;
    }
  }
  return `<h2>Retrain <span class="muted">${r.cost} gp · ${r.used ? 'used since you returned' : 'one swap per return to town'}</span></h2>
    <div class="note">A tutor will teach you a different feat, skill increase or spell in place of one you chose. Ancestry, background, class and attribute boosts can't be retrained.</div>
    ${r.blocker ? `<div class="note ${r.used ? '' : 'bad'}">${esc(r.blocker)}</div>` : ''}
    <div class="list retrain">${targets || '<span class="muted">Nothing you chose can be retrained yet.</span>'}</div>${options}`;
}

/**
 * The room's world in town: its premise and god, or (before it is made) the theme box, the line
 * while it is being made, and the vote to start a new one. The vote's clock ticks in place (`.wv-time`).
 */
function worldHtml(view: PlayerView): string {
  const w = view.hub?.world;
  if (!w) return '';
  const parts: string[] = [];
  if (w.premise) {
    parts.push(`<div class="world-premise"><b>This dungeon.</b> ${esc(w.premise)}${w.god ? ` <span class="muted">Its god is ${esc(w.god)}.</span>` : ''}</div>`);
  } else if (w.needsAi) {
    parts.push('<p class="muted world-note">This dungeon has no world of its own: making one needs the AI, which this server is not running. Every floor is drawn by chance.</p>');
  }
  if (w.founding) {
    parts.push('<div class="world-status" role="status">The dungeon stirs… a world is taking shape.</div>');
  } else if (w.canTheme) {
    const value = store.themeDraft ?? w.theme;
    parts.push(`<form class="hub-top world-theme" data-theme-form>
      <label>What should this dungeon be about? <input type="text" maxlength="200" data-theme-input data-keep-focus="theme" value="${esc(value)}" placeholder="Leave it blank and the dungeon decides"></label>
      <button type="submit" class="small">Set Theme</button>
      <span class="muted">${w.theme ? `Wished for: “${esc(w.theme)}”. ` : ''}The world is made when the party first readies up.</span>
    </form>`);
  }
  const v = w.vote;
  if (v) {
    const tally = `<span class="wv-tally" title="Votes needed: ${v.needed} of ${v.voters}">New world ${v.yes}/${v.needed}${v.no ? ` · Keep ${v.no}` : ''}</span>`;
    parts.push(`<div class="hub-top world-vote" role="alert">
      <span>${esc(v.byName)} calls a vote to start a new world. This one would be gone for good.</span>${tally}
      <span class="muted wv-time" title="Seconds left to vote"></span>
      <button type="button" class="small ${v.yourVote === 'yes' ? 'primary' : ''}" data-world-vote="yes">New World</button>
      <button type="button" class="small ${v.yourVote === 'no' ? 'primary' : ''}" data-world-vote="no">Keep This One</button>
    </div>`);
  } else if (w.canVote) {
    parts.push('<div class="hub-top"><button type="button" class="small" data-new-world title="Call a vote to let this world go and have a new one made">Start a New World</button><span class="muted">A majority of the players must agree. Its factions, its god and its memory are gone for good.</span></div>');
  }
  return parts.length ? `<div class="world-box">${parts.join('')}</div>` : '';
}

/** The town panel's HTML: party readiness, the delve picker, then the shop. Holds still between views unless something in it changed. */
export function hubHtml(view: PlayerView): string {
  if (view.phase !== 'hub' || !view.hub) return '';
  const you = view.party.find((p) => p.isYou);
  const gold = view.sheet?.gold ?? 0;
  const picks = (view.pending?.hands.filter((h) => h.pick && !h.temporary).length ?? 0) + (view.pending?.boostsLeft ?? 0);
  return `
    <h1>Town</h1>
    ${view.hub.lastRun ? `<div class="last-run">${esc(view.hub.lastRun)}</div>` : '<p class="muted">The dungeon entrance lies below the village. Buy light sources and supplies before you enter.</p>'}
    ${worldHtml(view)}
    <div class="hub-top">
      ${view.sheet ? `<button class="primary" data-ready="${!you?.ready}">${you?.ready ? 'Not Ready' : 'Ready'}${picks ? `<span class="badge" title="Choices you still have to make before you can delve">${picks}</span>` : ''}</button>` : '<span class="muted">Select a character to play.</span>'}
      <span class="muted">The party enters the dungeon when every connected player is ready. You keep the gold you find only if you leave the dungeon alive.</span>
    </div>
    ${view.hub.gate ? `<div class="gate-note" role="alert">⚠ ${esc(view.hub.gate)}.</div>` : ''}
    ${picks ? `<div class="hub-top"><button data-open-picks>★ ${picks} choice${picks === 1 ? '' : 's'} to make</button><span class="muted">Feats, spells, skill increases and attribute boosts must be chosen before you can delve.</span></div>` : ''}
    ${view.hub.rebuild ? `<div class="hub-top"><button data-rebuild title="Build this character again, step by step, keeping its gear, gold and experience">Rebuild Character</button><span class="muted">Free until your first delve: change ancestry, background, class and every choice.</span></div>` : ''}
    ${retrainHtml(view)}
    ${delvePicker(view.hub.delve)}
    <div class="hub-top" style="margin-top:8px">
      <button data-addbot title="Add a party member controlled by the computer">Add Bot Companion</button>
      ${view.party.some((p) => p.playerId.startsWith('bot_')) ? '<button data-removebots>Dismiss Bots</button>' : ''}
    </div>
    ${view.hub.enchanting ? `<div class="hub-top">${ENCHANT_HELPER_BUTTON}<span class="muted">Your gear's prefixes and suffixes, every enchant scroll in your pack and vault that fits, and the bridging each needs.</span></div>` : ''}
    ${vaultHtml(view.bank)}
    <h2>Shop <span class="muted">${gold} gp</span></h2>
    ${specialsHtml(view.hub.specials)}
    ${shopListHtml(view, view.hub)}
  `;
}

export function renderHub(view: PlayerView): void {
  const el = document.getElementById('hub')!;
  if (store.retrain && !view.hub?.retrain?.targets.some((t) => t.kind === store.retrain!.kind && t.id === store.retrain!.remove)) store.retrain = null;
  const vote = view.hub?.world.vote;
  // The retrain panel's pick list keeps its place while a feat's details open, as the level-up modal's does.
  keepScroll(el, ['[data-pl-box="retrain"] .pl-list'], () => keepSearchFocus(el, () => setHtml(el, hubHtml(view), () => setText(el.querySelector('.wv-time'), vote ? `${vote.secondsLeft}s` : ''))));
  // A re-render while typing a theme keeps the words (`store.themeDraft`) and focus; put the cursor back at the end.
  const input = el.querySelector<HTMLInputElement>('[data-theme-input]');
  if (input && document.activeElement === input && input.selectionStart === 0 && input.value) input.setSelectionRange(input.value.length, input.value.length);
}

// ------------------------------------------------------------ level-up modal

/** Runs a panel render and puts the cursor back in its search box if it was typing there (a re-render replaces the input). */
export function keepSearchFocus(el: Element, render: () => void): void {
  const active = document.activeElement as HTMLInputElement | null;
  const keep = active && el.contains(active) && active.dataset?.plField === 'search' ? { key: active.dataset.pl, start: active.selectionStart, end: active.selectionEnd } : null;
  render();
  if (!keep) return;
  const input = el.querySelector<HTMLInputElement>(`[data-pl="${keep.key}"][data-pl-field="search"]`);
  if (!input || document.activeElement === input) return;
  input.focus();
  if (keep.start !== null && keep.end !== null) input.setSelectionRange(keep.start, keep.end);
}

/**
 * Runs a panel render and puts its scrolling parts (the modal box, a pick list) back where they were
 * scrolled: a re-render that changes the panel's HTML (opening a feat's details) replaces them, which
 * would jump them back to the top.
 */
export function keepScroll(el: Element, selectors: readonly string[], render: () => void): void {
  const tops = selectors.map((s) => el.querySelector(s)?.scrollTop ?? 0);
  render();
  selectors.forEach((s, i) => {
    const box = el.querySelector(s);
    if (box && tops[i] && box.scrollTop !== tops[i]) box.scrollTop = tops[i];
  });
}

/** The scrolling parts of the level-up pick modal. */
const PICK_SCROLLERS = ['.modal-box', '.pl-list'] as const;

/** The options of the level-up pick on screen, and how to list them (the data-card click takes one). */
export function pickModalList(view: PlayerView): { opts: PickOpt[]; cfg: ListCfg } | null {
  const hand = view.pending?.hands[0];
  if (!hand?.pick) return null;
  const opts = hand.options ?? [];
  const cfg: ListCfg = { spell: hand.kind === 'spell', codex: store.welcome?.codex ?? null, mode: 'focus', attr: 'card', value: (id) => `${hand.id}:${id}`, takeLabel: hand.kind === 'spell' ? 'Learn This Spell' : hand.kind === 'skill' ? 'Increase This Skill' : 'Take This Feat' };
  return { opts, cfg };
}

/** How many attributes the current boost set still needs chosen. */
export function boostsNeeded(view: PlayerView): number {
  const left = view.pending?.boostsLeft ?? 0;
  return left ? Math.min(left, left % 4 || 4) : 0;
}

export function renderModal(view: PlayerView): void {
  const el = document.getElementById('modal')!;
  const hand = view.pending?.hands[0];
  const boostsLeft = view.pending?.boostsLeft ?? 0;
  if (!boostsLeft && (store.boost.open || store.boost.later || store.boost.chosen.length)) store.boost = { open: false, chosen: [], later: false };
  const showHand = !!hand && store.modalDismissedFor !== hand.id;
  const showBoost = !showHand && boostsLeft > 0 && (store.boost.open || !store.boost.later);
  if (!showHand && !showBoost) {
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  if (!showHand) return keepSearchFocus(el, () => setHtml(el, boostModalHtml(view)));
  const h = hand!;
  const spellHand = h.kind === 'spell';
  const skillHand = h.kind === 'skill';
  const title = spellHand
    ? h.title ?? 'Learn a Spell'
    : skillHand ? `Level ${view.sheet?.level ?? ''}: Skill Increase`
    : h.title ?? (h.source === 'shrine' ? 'Shrine: Select a Temporary Feat' : h.source === 'feat' ? 'Select a Bonus Feat' : `Level ${view.sheet?.level ?? ''}: Select a Feat`);
  const sub = h.subtitle ?? (spellHand
    ? 'Select 1 spell to add to your spell repertoire.'
    : skillHand ? 'Select 1 skill. Its proficiency rank increases by one step.'
    : h.temporary ? 'Select 1 feat. You lose it when you leave the dungeon.' : 'Select 1 feat.');
  const more = (view.pending?.hands.length ?? 1) - 1;
  const note = more > 0 ? `<span class="muted grow">${more} more choice${more === 1 ? '' : 's'} waiting after this one.</span>` : '<span class="grow"></span>';
  const picker = pickModalList(view);
  if (picker) {
    // A new pick starts at the top; the same one keeps its place while a feat's details open and close.
    const samePick = store.pickUi.handId === h.id;
    if (!samePick) store.pickUi = { handId: h.id, list: newListState() };
    const state = store.pickUi.list;
    return keepScroll(el, samePick ? PICK_SCROLLERS : [], () => keepSearchFocus(el, () => setHtml(el, `
      <div class="modal-box pick-box">
        <h1>${esc(title)}</h1>
        <p class="muted">${esc(sub)}</p>
        <div class="pl" data-pl-box="pick">${controlsHtml(picker.opts, state, picker.cfg, 'pick')}<div class="pl-list">${listHtml(picker.opts, state, picker.cfg)}</div></div>
        <div class="modal-actions">${note}<button data-later="${esc(h.id)}">Decide Later</button></div>
      </div>`)));
  }
  // A shrine's hand: random cards, as ever.
  const codex = store.welcome?.codex;
  const cards = h.cards.map((c) => {
    const block = codex?.[c.ref];
    const body = block
      ? blockHtml(block, { compact: true })
      : `<span class="title">${esc(c.name)}</span><span class="desc">${esc(c.description)}</span>`;
    return `<button class="card ${esc(c.rarity)} ${spellHand ? 'spell' : ''}" data-card="${esc(h.id)}:${esc(c.id)}">${body}</button>`;
  }).join('');
  setHtml(el, `
    <div class="modal-box">
      <h1>${esc(title)}</h1>
      <p class="muted">${esc(sub)}</p>
      <div class="cards">${cards}</div>
      <div class="modal-actions">
        <button data-later="${esc(h.id)}">Decide Later</button>
      </div>
    </div>`);
}

/** The attribute boost picker: a set of boosts goes to different attributes. */
function boostModalHtml(view: PlayerView): string {
  const need = boostsNeeded(view);
  const s = view.sheet;
  const can = view.pending?.boostable ?? [];
  const chosen = store.boost.chosen.filter((a) => can.includes(a));
  const buttons = ATTRIBUTES.map((a) => {
    const value = s?.attributes[a] ?? 0;
    const partial = s?.partialBoosts.includes(a);
    const on = chosen.includes(a);
    const result = value >= 4 ? (partial ? `${signed(value + 1)}` : 'partial boost') : signed(value + 1);
    return `<button type="button" class="choice b-attr ${on ? 'selected' : ''}" ${can.includes(a) ? '' : 'disabled'} data-boost-pick="${a}"><b>${a.toUpperCase()}</b><span>${esc(ATTRIBUTE_NAMES[a])} ${signed(value)}${partial ? '½' : ''} → ${esc(result)}</span></button>`;
  }).join('');
  return `
    <div class="modal-box boost-box">
      <h1>Level ${s?.level ?? ''}: Attribute Boosts</h1>
      <p class="muted">Choose ${need} different attribute${need === 1 ? '' : 's'} to boost. From +4 up a boost is partial: two partial boosts make +1.</p>
      <div class="b-attrs">${buttons}</div>
      <div class="muted b-hint">${chosen.length}/${need} chosen</div>
      <div class="modal-actions"><span class="grow"></span>
        <button data-boost-later>Decide Later</button>
        <button class="primary" data-boost-apply ${chosen.length === need ? '' : 'disabled'}>Apply ${need} Boost${need === 1 ? '' : 's'}</button>
      </div>
    </div>`;
}

// ------------------------------------------------------------ toasts, banner, tooltip

const TOAST_MS = 3500;
const MAX_TOASTS = 3;
const shownToasts = new Map<string, { el: HTMLElement; count: number; timer: number }>();

/**
 * An error or notice at the bottom of the screen. The same message again while it's still showing
 * bumps a counter on it instead of stacking another, and only the newest few stay up.
 */
export function toast(message: string): void {
  const box = document.getElementById('toasts')!;
  const shown = shownToasts.get(message);
  const hide = (el: HTMLElement) => window.setTimeout(() => {
    el.remove();
    if (shownToasts.get(message)?.el === el) shownToasts.delete(message);
  }, TOAST_MS);
  if (shown && shown.el.isConnected) {
    shown.count += 1;
    shown.el.textContent = `${message} (×${shown.count})`;
    window.clearTimeout(shown.timer);
    shown.timer = hide(shown.el);
    return;
  }
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = message;
  box.appendChild(t);
  shownToasts.set(message, { el: t, count: 1, timer: hide(t) });
  while (box.children.length > MAX_TOASTS) box.firstElementChild!.remove();
}

let bannerTimer = 0;
/** A word across the map for a moment; `turn` is the big, glowing one that says your turn has come. */
export function banner(text: string, kind?: 'turn'): void {
  const el = document.getElementById('banner')!;
  el.textContent = text;
  el.classList.toggle('turn', kind === 'turn');
  el.classList.remove('hidden');
  // Restart the entrance animation when one banner follows another.
  el.style.animation = 'none';
  void el.offsetWidth;
  el.style.animation = '';
  clearTimeout(bannerTimer);
  bannerTimer = window.setTimeout(() => el.classList.add('hidden'), kind === 'turn' ? 2200 : 1800);
}

/** Does the creature's space cover this square? (A Large creature covers four.) */
export function covers(c: CreatureView, x: number, y: number): boolean {
  const s = c.size ?? 1;
  return x >= c.x && y >= c.y && x < c.x + s && y < c.y + s;
}

/** What the party recalls about a creature's kind, a line per fact; a ✓ marks one known to be true (a critical success). */
export function loreHtml(c: CreatureView): string {
  if (!c.lore?.length) return '';
  const lines = c.lore.map((l) => `<div>${l.sure ? '<span class="lore-sure" title="Recalled on a critical success: known to be true">✓</span> ' : ''}<b>${esc(l.label)}</b> ${esc(l.text)}</div>`);
  return `<div class="lore">${lines.join('')}</div>`;
}

/** What the room's Artifacts of Change made of a creature, on its tooltip: "deals fire damage, resists cold 3". */
export function changedLine(c: CreatureView): string {
  return c.changed ? `<br><span class="changed" title="Changed by an Artifact of Change">◈ ${esc(c.changed.replace(/^./, (ch) => ch.toUpperCase()))}</span>` : '';
}

/** A party member's Stealth on its tooltip: "Hidden from 2 and undetected by 1 of the foes the party can see." */
export function sneakingLine(c: CreatureView): string {
  const s = c.sneaking;
  const lost = s ? lostFoesText(s.hidden, s.undetected) : '';
  return lost ? `<br><span class="muted">${esc(lost.replace(/^./, (ch) => ch.toUpperCase()))} of the foes the party can see.</span>` : '';
}

/**
 * Whose a creature is, on its tooltip: a known faction by name with your standing among them ("Red Claw · Liked"),
 * an unknown one as "Unknown band", or "Wild", with the faction's colour swatch. Nothing outside a world. A thinking
 * member not fighting you also says whether it would join you: "easier to win over (DC -2)", or "won't join you".
 */
export function allegianceLine(c: CreatureView): string {
  const a = c.allegiance;
  if (!a) return '';
  const swatch = a.hue !== undefined ? `<span class="faction-swatch" style="background:${factionColor(a.hue)}"></span>` : '';
  const join = a.wontJoin ? ' · won\'t join you' : a.recruitDc ? ` · easier to win over (DC ${a.recruitDc})` : '';
  const text = a.known ? `${esc(a.name)}${a.tier ? ` · ${esc(a.tier)}` : ''}${join}` : a.hue !== undefined ? `${esc(a.name)}: you don't know whose it is` : `${esc(a.name)}: no faction`;
  return `<br><span class="muted allegiance">${swatch}${text}</span>`;
}

export function tooltipFor(view: PlayerView, x: number, y: number): string {
  const d = view.dungeon;
  if (!d) return '';
  const parts: string[] = [];
  for (const c of d.creatures.filter((c) => covers(c, x, y))) {
    const hp = c.hp !== undefined ? `${c.hp}/${c.maxHp} HP` : HEALTH_LABEL[c.health];
    const kind = c.kind ? `${c.kind.toLowerCase()} · ` : '';
    const mind = c.mind === 'animal' ? '<br><span class="muted">An animal: it doesn\'t understand words.</span>'
      : c.mind === 'mindless' && !c.construct ? '<br><span class="muted">Mindless: words mean nothing to it.</span>' : '';
    if (c.faction === 'neutral') {
      const att = c.surrendered ? 'surrendered' : c.attitude ?? 'not hostile';
      parts.push(`<b>${esc(c.name)}</b> <span class="muted">${esc(kind)}${esc(att)}</span>${allegianceLine(c)}${c.status.length ? `<br>${esc(statusLine(c))}` : ''}${changedLine(c)}${mind || `<br><span class="muted">Not attacking. Talk to it: pick it in the chat (or Alt+click it)${c.allegiance?.recruitDc !== undefined ? '; ask it to join you ("Join us!")' : ''}. Attacking it makes it hostile.</span>`}${loreHtml(c)}`);
      continue;
    }
    if (c.ally) {
      const talk = c.construct ? 'A construct companion: it acts only when its inventor Commands it. Alt+click to talk to it.' : 'Alt+click to talk to it alone.';
      parts.push(`<b>${esc(c.name)}</b> <span class="muted">${esc(kind)}ally${c.attitude ? `, ${esc(c.attitude)}` : ''} · ${esc(healthLabel(c))}</span>${allegianceLine(c)}${c.status.length ? `<br>${esc(statusLine(c))}` : ''}${changedLine(c)}${sneakingLine(c)}<br><span class="muted">${talk}</span>${loreHtml(c)}`);
      continue;
    }
    const what = c.detection === 'hidden'
      ? 'Hidden: you know which square it is in but can\'t see it. DC 11 flat check to target it.'
      : c.detection === 'concealed' ? 'Concealed (dim light): DC 5 flat check to target it.' : '';
    const talk = !c.isPc && c.mind === 'sapient' && c.detection !== 'hidden' ? '<br><span class="muted">It thinks for itself: Alt+click to talk to it.</span>' : '';
    const companion = c.bot
      ? `<br><span class="muted">Companion: it speaks only when spoken to (Alt+click to talk to it).${c.waiting ? ' Waiting: it holds here until told to follow.' : ''}</span>` : '';
    const boss = c.boss ? '<br><span class="bad">Boss: it guards the stairs down.</span>' : '';
    parts.push(`<b>${esc(c.name)}</b>${c.level !== undefined && !c.isPc ? ` <span class="muted">${esc(kind)}Creature ${c.level < 0 ? `–${-c.level}` : c.level}</span>` : ''} · ${esc(hp)}${boss}${allegianceLine(c)}${c.status.length ? `<br>${esc(statusLine(c))}` : ''}${changedLine(c)}${sneakingLine(c)}${what ? `<br><span class="muted">${what}</span>` : ''}${c.isPc ? companion : mind || talk}${loreHtml(c)}`);
  }
  for (const f of d.features.filter((f) => f.x === x && f.y === y)) {
    // Touching an artifact can't be undone: say so before anyone does.
    const warn = f.kind === 'artifact-of-change' && f.state === 'humming'
      ? '<br><span class="changed">Touching it changes this dungeon for good, for every delve from this room: its creatures or the party, by chance. It spills gold too.</span>' : '';
    parts.push(`<b>${esc(f.name)}</b> <span class="muted">${esc(f.state ?? '')}</span>${warn}`);
  }
  const code = d.terrain[y * d.width + x];
  const hazard = code && code !== ' ' ? d.terrainLegend[code] : undefined;
  if (hazard) parts.push(`<b>${esc(hazard.name)}</b> <span class="muted">${esc(hazard.skills.join(' or '))}</span>`);
  for (const h of (d.hazards ?? []).filter((h) => h.x === x && h.y === y)) {
    const disable = h.live && h.disable?.length ? `<br><span class="muted">Disable a Device: ${esc(h.disable.join(' or '))}</span>` : '';
    parts.push(`<b>${esc(h.name)}</b> <span class="${h.live ? 'bad' : 'muted'}">${h.live ? 'Hazard' : 'Hazard (no longer a danger)'}</span>${h.description ? `<br><span class="muted">${esc(h.description)}</span>` : ''}${disable}`);
  }
  for (const i of d.items.filter((i) => i.x === x && i.y === y)) parts.push(`${esc(i.name)}${i.qty > 1 ? ` ×${i.qty}` : ''}${i.lit ? ' (lit)' : ''}`);
  const v = d.vis[y * d.width + x];
  if (!parts.length && d.tiles[y * d.width + x] !== ' ') {
    parts.push(`<span class="muted">${v === '2' ? 'Bright light' : v === '1' ? 'Dim light' : v === '3' ? 'Darkness (adjacent)' : 'Explored, not in view'}</span>`);
  }
  return parts.join('<hr>');
}
